import {
  getEndOfDayLoadModeLongLabel,
  getEndOfDayLoadModeShortLabel,
} from "../utils/endOfDayLoadModeLabels.js";
import { resolveOrderDeliveredByDriverSummary } from "../utils/dataManagerDeliveryDrivers.js";
import { resolveEndOfDayLoadTrailerWords } from "../utils/fleetReassignmentHelpers.js";

const LOADING_CLIENT_SUGGESTION_MIN_LENGTH = 3;
const LOADING_CLIENT_SUGGESTION_LIMIT = 8;
const ANCHORED_ADDRESS_TOAST_MARGIN_PX = 12;
const ANCHORED_ADDRESS_TOAST_ESTIMATED_HEIGHT_PX = 148;
const ANCHORED_ADDRESS_TOAST_ESTIMATED_WIDTH_PX = 548;
export const DATA_MANAGER_RECENT_BROWSE_ORDERS_LIMIT = 50;

let createOrderDeliveryDraftSequence = 0;

function createEmptyCreateOrderDelivery() {
  createOrderDeliveryDraftSequence += 1;

  return {
    aspirator: "",
    ontario: "non",
    address: "",
    building: "",
    comment: "",
    delivery_priority: "",
    delivery_window_start: "",
    draft_id: `create-delivery-${createOrderDeliveryDraftSequence}`,
    client_name: "",
    grain_code: "",
    grain_type: "",
    loading_priority: "",
    loading_silo: "",
    loading_window_start: "",
    loading_tonne_rate_override: "",
    quantity_tons: "",
    unloading_tonne_rate_override: "",
    work_order_number: "",
  };
}

export function createNextCreateOrderDelivery(previousDelivery = null) {
  const nextDelivery = createEmptyCreateOrderDelivery();
  const previousClientName = typeof previousDelivery?.client_name === "string"
    ? previousDelivery.client_name.trim()
    : "";
  const previousUnloadingTonneRate = typeof previousDelivery?.unloading_tonne_rate_override === "string"
    ? previousDelivery.unloading_tonne_rate_override.trim()
    : "";
  const shouldCopyUnloadingTonneRate = previousClientName !== "" && previousUnloadingTonneRate !== "";

  return {
    ...nextDelivery,
    address: previousDelivery?.address ?? nextDelivery.address,
    client_name: previousDelivery?.client_name ?? nextDelivery.client_name,
    grain_type: previousDelivery?.grain_type ?? nextDelivery.grain_type,
    ...(shouldCopyUnloadingTonneRate
      ? { unloading_tonne_rate_override: previousDelivery.unloading_tonne_rate_override }
      : {}),
  };
}

export function normalizeOptionalString(value) {
  const resolvedValue = typeof value === "string" ? value.trim() : "";
  return resolvedValue || null;
}

export function normalizeOptionalStringList(value) {
  if (Array.isArray(value)) {
    return value
      .map((entry) => normalizeOptionalString(entry))
      .filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((entry) => normalizeOptionalString(entry))
      .filter(Boolean);
  }
  return [];
}

function normalizeComparableStringList(value) {
  return [...new Set(normalizeOptionalStringList(value))].sort((left, right) => left.localeCompare(right));
}

function normalizeOrderedStringList(value) {
  return [...new Set(normalizeOptionalStringList(value))];
}

function stringListsMatch(left, right) {
  const normalizedLeft = normalizeComparableStringList(left);
  const normalizedRight = normalizeComparableStringList(right);
  return normalizedLeft.length === normalizedRight.length
    && normalizedLeft.every((value, index) => value === normalizedRight[index]);
}

export function normalizeCreateOrderDeliveries(deliveries) {
  if (!Array.isArray(deliveries) || deliveries.length === 0) {
    return [createEmptyCreateOrderDelivery()];
  }

  return deliveries.map((delivery) => ({
    ...(typeof delivery?.allow_missing_civic_number === "boolean" ? { allow_missing_civic_number: delivery.allow_missing_civic_number } : {}),
    aspirator: normalizeAspiratorDraftValue(delivery?.aspirator),
    ontario: normalizeOntarioDraftValue(delivery?.ontario),
    address: delivery?.address ?? "",
    building: delivery?.building ?? "",
    comment: delivery?.comment ?? "",
    delivery_priority: normalizeLoadingPriorityDraftValue(delivery?.delivery_priority),
    delivery_window_start: toTimeInputValue(delivery?.delivery_window_start),
    draft_id: normalizeOptionalString(delivery?.draft_id) || createEmptyCreateOrderDelivery().draft_id,
    client_name: delivery?.client_name ?? "",
    grain_code: delivery?.grain_code ?? "",
    grain_type: delivery?.grain_type ?? "",
    loading_priority: normalizeLoadingPriorityDraftValue(delivery?.loading_priority),
    loading_silo: delivery?.loading_silo ?? "",
    loading_window_start: toTimeInputValue(delivery?.loading_window_start),
    loading_tonne_rate_override: delivery?.loading_tonne_rate_override == null ? "" : String(delivery.loading_tonne_rate_override),
    quantity_tons: delivery?.quantity_tons == null ? "" : String(delivery.quantity_tons),
    unloading_tonne_rate_override: delivery?.unloading_tonne_rate_override == null ? "" : String(delivery.unloading_tonne_rate_override),
    work_order_number: delivery?.work_order_number ?? "",
  }));
}

export function getCreateOrderDeliveries(draft = null) {
  return normalizeCreateOrderDeliveries(draft?.deliveries);
}

export function getPrimaryCreateOrderDelivery(draft = null) {
  return getCreateOrderDeliveries(draft)[0] || null;
}

export function findCreateOrderDelivery(draft, deliveryDraftId) {
  return getCreateOrderDeliveries(draft).find((delivery) => delivery.draft_id === deliveryDraftId) || null;
}

const DISPATCH_STATUS_META = {
  assigned: { label: "Affectée", className: "border border-slate-200 bg-slate-100 text-slate-700" },
  cancelled: { label: "Annulée", className: "border border-rose-200 bg-rose-50 text-rose-700" },
  completed: { label: "Livrée", className: "border border-sky-200 bg-sky-50 text-sky-700" },
  ready: { label: "Prête", className: "border border-emerald-200 bg-emerald-50 text-emerald-700" },
};

const EMPTY_NEW_ORDER_DRAFT = {
  address: "",
  aspirator: "",
  ontario: "non",
  building: "",
  client_code: "",
  client_name: "",
  comment: "",
  coop: "",
  deliveries: [],
  delivery_priority: "",
  delivery_date: "",
  delivery_window_start: "",
  grain_code: "",
  grain_type: "",
  is_preloaded_previous_day: "non",
  delivery_period: "non",
  is_dompeur: "non",
  loading_client_name: "",
  loading_silo: "",
  loading_priority: "",
  loading_window_start: "",
  loading_tonne_rate_override: "",
  hourly_rate_override: "",
  fixed_rate_override: "",
  order_id: "",
  order_date: "",
  quantity_tons: "",
  unloading_tonne_rate_override: "",
  work_order_number: "",
};

export function createNewOrderDraft() {
  return {
    ...EMPTY_NEW_ORDER_DRAFT,
    deliveries: normalizeCreateOrderDeliveries(),
    order_date: formatDateInput(new Date()),
  };
}

function normalizeLegacyTariffColumnPreferences(preferences) {
  const order = preferences?.column_order;
  if (!Array.isArray(order) || (!order.includes("unloading_tonne_rate_override") && order.includes("hourly_rate_override"))) {
    return preferences;
  }
  const anchor = order.includes("loading_tonne_rate_override") ? "loading_tonne_rate_override" : "unloading_tonne_rate_override";
  if (!order.includes(anchor)) return preferences;
  return {
    ...preferences,
    column_order: order.flatMap((key) => key === anchor
      ? ["hourly_rate_override", "loading_tonne_rate_override"]
      : ["hourly_rate_override", "loading_tonne_rate_override", "unloading_tonne_rate_override"].includes(key) ? [] : [key]),
    frozen_column_keys: preferences.frozen_column_keys?.filter((key) => key !== "unloading_tonne_rate_override"),
  };
}

export function normalizeOrderTariffColumnPreferences(preferences) {
  const resolved = normalizeLegacyTariffColumnPreferences(preferences);
  const order = resolved?.column_order;
  if (!Array.isArray(order)) return resolved;
  // Columns added later join saved orders: the fixed rate after the $/t exception, the loading reference after the loading client.
  const added = { loading_tonne_rate_override: "fixed_rate_override", loading_client_name: "loading_reference" };
  const next = order.flatMap((key) => added[key] && !order.includes(added[key]) ? [key, added[key]] : [key]);
  return next.length === order.length ? resolved : { ...resolved, column_order: next };
}

export const ORDER_COMMENT_COLUMN = {
  key: "comment",
  label: "Commentaire",
  placeholder: "Commentaire",
  type: "text",
  widthClassName: "w-40",
  defaultWidth: 160,
  minWidth: 120,
};

const ORDER_ACTUAL_LOADED_QUANTITY_COLUMN = {
  key: "actual_loaded_quantity_tons",
  label: "Chargé réel t",
  type: "readonly-number",
  widthClassName: "w-18",
  defaultWidth: 72,
  minWidth: 60,
};

const ORDER_ACTUAL_DELIVERED_QUANTITY_COLUMN = {
  key: "actual_delivered_quantity_tons",
  label: "Livré réel t",
  type: "readonly-number",
  widthClassName: "w-18",
  defaultWidth: 72,
  minWidth: 60,
};

// Bulles des exceptions de tarif : mêmes textes dans la grille et dans « Ajouter une commande ».
export const ORDER_RATE_OVERRIDE_HELP = Object.freeze({
  hourly: "Vide : tarif horaire de l’entreprise. 0 : exception horaire nulle. S’applique à la livraison ; le chargement, l’inspection et le retour gardent le tarif normal. Camion dompeur : sans effet.",
  tonne: "Vide : réglage de l’entreprise. 0 : aucun supplément. Une valeur positive s’ajoute au montant horaire sur les tonnes réellement livrées. Camion dompeur : prix par tonne livrée, à la place des heures et du tarif dompeur des Paramètres.",
});

export const ORDER_GRID_COLUMNS = [
  { key: "delivery_date", label: "Date livraison", type: "date", widthClassName: "w-40", defaultWidth: 160, minWidth: 144 },
  { key: "order_id", label: "Bon commande", type: "readonly", widthClassName: "w-32", defaultWidth: 128, minWidth: 96 },
  { key: "work_order_number", label: "Bon travail", placeholder: "OT-2026-001", type: "text", widthClassName: "w-20", defaultWidth: 80, minWidth: 64 },
  { key: "client_name", label: "Client déchargement", placeholder: "Client", type: "text", widthClassName: "w-36", defaultWidth: 154, minWidth: 145 },
  { key: "building", label: "Bâtiment", placeholder: "Bâtiment", type: "text", widthClassName: "w-20", defaultWidth: 88, minWidth: 72 },
  { key: "loading_silo", label: "Silo", type: "text", widthClassName: "w-20", defaultWidth: 80, minWidth: 64 },
  { key: "grain_code", label: "Code produit", type: "text", widthClassName: "w-32", defaultWidth: 128, minWidth: 96 },
  { key: "quantity_tons", label: "Nb tm", min: "0", step: "0.1", type: "number", widthClassName: "w-18", defaultWidth: 72, minWidth: 60 },
  { key: "hourly_rate_override", label: "Exception $/h", help: ORDER_RATE_OVERRIDE_HELP.hourly, min: "0", step: "0.0001", type: "number", widthClassName: "w-28", defaultWidth: 110, minWidth: 96 },
  { key: "loading_tonne_rate_override", label: "Exception $/t", help: ORDER_RATE_OVERRIDE_HELP.tonne, min: "0", step: "0.0001", type: "number", widthClassName: "w-28", defaultWidth: 110, minWidth: 96 },
  { key: "fixed_rate_override", label: "Exception $ fixe", help: "Vide : aucun montant, sauf une Aspiration qui reçoit le tarif de Facturation. 0 : aucun montant. Une valeur positive s’ajoute au montant horaire de la commande.", min: "0", step: "0.0001", type: "number", widthClassName: "w-28", defaultWidth: 110, minWidth: 96 },
  ORDER_ACTUAL_LOADED_QUANTITY_COLUMN,
  ORDER_ACTUAL_DELIVERED_QUANTITY_COLUMN,
  { key: "grain_type", label: "Produit", placeholder: "Poulet", type: "text", widthClassName: "w-16", defaultWidth: 64, minWidth: 56 },
  { key: "delivery_period", label: "Soir", type: "select", widthClassName: "w-16", defaultWidth: 64, minWidth: 56 },
  { key: "loading_client_name", label: "Client Chargement", placeholder: "Nom client", type: "readonly-loading-summary", widthClassName: "w-40", defaultWidth: 130, minWidth: 100 },
  // Enregistré à part, à la sortie du champ, comme dans Consolidation (DataManagerOrdersGridRow).
  { key: "loading_reference", label: "N° de référence", type: "loading-reference", widthClassName: "w-28", defaultWidth: 112, minWidth: 88 },
  { key: "coop", label: "Facturer à", placeholder: "Entreprise facturée", type: "text", widthClassName: "w-36", defaultWidth: 144, minWidth: 96 },
  { key: "client_code", label: "# client", placeholder: "SUP-M01", type: "text", widthClassName: "w-22", defaultWidth: 88, minWidth: 72 },
  { key: "end_of_day_load_action", label: "PM", type: "readonly-end-of-day-load-action", widthClassName: "w-28", defaultWidth: 112, minWidth: 96 },
  { key: "address", label: "Adresse déchargement", placeholder: "Adresse de déchargement", type: "text", widthClassName: "w-48", defaultWidth: 192, minWidth: 144 },
  { key: "aspirator", label: "Aspirateur", type: "select", widthClassName: "w-18", defaultWidth: 72, minWidth: 56 },
  { key: "ontario", label: "Ontario", type: "select", widthClassName: "w-18", defaultWidth: 72, minWidth: 56 },
  { key: "is_dompeur", label: "Dompeur", type: "select", widthClassName: "w-16", defaultWidth: 64, minWidth: 56 },
  { key: "loading_window_start", label: "Début chargement", type: "time", widthClassName: "w-18", defaultWidth: 72, minWidth: 120 },
  { key: "loading_priority", label: "Priorité chargement", type: "select", widthClassName: "w-18", defaultWidth: 72, minWidth: 64 },
  { key: "delivery_window_start", label: "Début déchargement", type: "time", widthClassName: "w-18", defaultWidth: 72, minWidth: 120 },
  { key: "delivery_priority", label: "Priorité déchargement", type: "select", widthClassName: "w-18", defaultWidth: 72, minWidth: 64 },
  { key: "is_preloaded_previous_day", label: "Chargé la veille", type: "select", widthClassName: "w-22", defaultWidth: 92, minWidth: 84 },
  ORDER_COMMENT_COLUMN,
];

export const ORDER_EXECUTION_DETAIL_COLUMNS = Object.freeze([
  ORDER_ACTUAL_LOADED_QUANTITY_COLUMN,
  ORDER_ACTUAL_DELIVERED_QUANTITY_COLUMN,
  { key: "delivered_by_driver_name", label: "Livré par", type: "readonly-delivery-driver", widthClassName: "w-28", defaultWidth: 112, minWidth: 88 },
]);

export const ORDER_SEARCH_HIGHLIGHT_COLUMN_KEYS = new Set([
  "delivery_date",
  "order_id",
  "work_order_number",
  "client_name",
  "building",
  "coop",
  "loading_silo",
  "grain_code",
  "grain_type",
  "quantity_tons",
  "delivered_by_driver_name",
]);

export const ORDER_GRID_SELECTION_COLUMN_WIDTH = 44;

export const ORDER_DISPATCH_COLUMN = {
  key: "dispatch_status",
  label: "Statut",
  defaultWidth: 96,
  minWidth: 88,
};

export const ORDER_BILLING_APPROVAL_COLUMN = {
  key: "billing_approval_status",
  label: "Facturation",
  defaultWidth: 112,
  minWidth: 96,
};

export const ORDER_ACTIONS_COLUMN = {
  key: "actions",
  label: "Actions",
  defaultWidth: 168,
  minWidth: 152,
};

export function getOrderGridTrailingColumns() {
  return [
    ORDER_DISPATCH_COLUMN,
    ORDER_BILLING_APPROVAL_COLUMN,
  ];
}

export const ORDER_GRID_RESIZABLE_COLUMNS = Object.freeze([
  ...ORDER_GRID_COLUMNS,
  ORDER_DISPATCH_COLUMN,
  ORDER_BILLING_APPROVAL_COLUMN,
  ORDER_ACTIONS_COLUMN,
]);

export const ORDER_GRID_COLUMN_DEFAULT_WIDTHS = Object.freeze(
  Object.fromEntries(ORDER_GRID_RESIZABLE_COLUMNS.map((column) => [column.key, column.defaultWidth]))
);

export const ORDER_GRID_COLUMN_MIN_WIDTHS = Object.freeze(
  Object.fromEntries(ORDER_GRID_RESIZABLE_COLUMNS.map((column) => [column.key, column.minWidth]))
);

export function normalizeOrderGridColumnWidths(widths = null) {
  const savedWidths = widths && typeof widths === "object" ? widths : {};

  return Object.fromEntries(ORDER_GRID_RESIZABLE_COLUMNS.map((column) => {
    const candidateWidth = Number(savedWidths[column.key]);
    const fallbackWidth = Number(column.defaultWidth) || 0;
    const minWidth = Number(column.minWidth) || 0;
    return [
      column.key,
      Math.max(minWidth, Math.round(Number.isFinite(candidateWidth) && candidateWidth > 0 ? candidateWidth : fallbackWidth)),
    ];
  }));
}

export function resolveOrderGridColumnWidth(columnKey = "", widths = null) {
  const candidateWidth = Number(widths?.[columnKey]);
  const fallbackWidth = Number(ORDER_GRID_COLUMN_DEFAULT_WIDTHS[columnKey]);
  const minWidth = Number(ORDER_GRID_COLUMN_MIN_WIDTHS[columnKey]) || 0;
  const resolvedWidth = Number.isFinite(candidateWidth) ? candidateWidth : fallbackWidth;
  return Math.max(minWidth, Math.round(Number.isFinite(resolvedWidth) ? resolvedWidth : minWidth));
}

export function buildOrderGridColumnStyle(width) {
  const resolvedWidth = Math.max(0, Math.round(Number(width) || 0));

  return {
    minWidth: `${resolvedWidth}px`,
    width: `${resolvedWidth}px`,
  };
}

export function getOrderGridTableWidth(widths = null, { includeSelectionColumn = true } = {}) {
  return (includeSelectionColumn ? ORDER_GRID_SELECTION_COLUMN_WIDTH : 0)
    + ORDER_GRID_RESIZABLE_COLUMNS.reduce(
      (totalWidth, column) => totalWidth + resolveOrderGridColumnWidth(column.key, widths),
      0,
    );
}

export const ASPIRATOR_SELECT_OPTIONS = [
  { value: "", label: "Vide (non)" },
  { value: "oui", label: "Oui" },
  { value: "non", label: "Non" },
];

export const PRELOADED_PREVIOUS_DAY_SELECT_OPTIONS = [
  { value: "oui", label: "Oui" },
  { value: "non", label: "Non" },
];

export const ORDER_LOADING_PRIORITY_OPTIONS = [
  { value: "", label: "Vide" },
  { value: "P1", label: "P1" },
  { value: "P2", label: "P2" },
  { value: "P3", label: "P3" },
];

export const ORDER_DELIVERY_SCOPE_TITLE = "Fenêtre de déchargement multi-livraison non éditable ici.";

export const ORDER_ADDRESS_FIELDS = ["address"];

export const ORDER_ADDRESS_FIELD_LABELS = {
  address: "Adresse de déchargement",
};

function normalizeDispatchStatusToken(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

export function canDeleteOrder(order, canDeleteDeliveredOrders = false) {
  if (!order?.order_id) {
    return false;
  }

  const dispatchStatus = normalizeDispatchStatusToken(order.dispatch_status);
  const dispatchDayState = normalizeDispatchStatusToken(order.dispatch_day_state);
  const dispatchBackendStatus = normalizeDispatchStatusToken(order.dispatch_backend_status);

  if (dispatchDayState === "closed") {
    return false;
  }

  if (dispatchBackendStatus === "livre" || dispatchBackendStatus === "cloture") {
    return false;
  }

  if (dispatchStatus === "completed") {
    return canDeleteDeliveredOrders;
  }

  return true;
}

export function resolveMutationErrorMessage(error, fallbackMessage) {
  return error instanceof Error && error.message
    ? error.message
    : fallbackMessage;
}

export function isOrderAddressField(fieldKey) {
  return ORDER_ADDRESS_FIELDS.includes(fieldKey);
}

export function isOrderAddressFocusKey(focusKey) {
  return focusKey === "delivery_address" || isOrderAddressField(focusKey);
}

export function getCreateAddressValidationKey(deliveryDraftId, fieldKey = "address") {
  return `create:${deliveryDraftId}:${fieldKey}`;
}

export function getOrderAddressValidationKey(orderId, fieldKey) {
  return `${orderId}:${fieldKey}`;
}

export function getOrderDeliveries(order = null) {
  return Array.isArray(order?.deliveries)
    ? order.deliveries.filter((delivery) => delivery && (delivery.client_name || delivery.address || delivery.delivery_id))
    : [];
}

export function resolveEditableSingleDelivery(order = null) {
  const deliveries = getOrderDeliveries(order);
  return deliveries.length === 1 ? deliveries[0] : null;
}

export function canEditSingleDeliveryWindowFields(order = null) {
  return getOrderDeliveries(order).length <= 1;
}

export function isDeliveryWindowField(fieldKey) {
  return fieldKey === "delivery_window_start" || fieldKey === "delivery_priority";
}

export function createEditableOrderDraft(order = null) {
  const deliveries = getOrderDeliveries(order);
  const singleDelivery = deliveries.length === 1 ? deliveries[0] : null;
  const uniqueDeliveryAddresses = Array.from(new Set(deliveries
    .map((delivery) => (typeof delivery?.address === "string" ? delivery.address.trim() : ""))
    .filter(Boolean)));
  const resolvedDeliveryAddress = uniqueDeliveryAddresses.length === 1
    ? uniqueDeliveryAddresses[0]
    : order?.address ?? "";

  return {
    address: resolvedDeliveryAddress,
    aspirator: normalizeAspiratorDraftValue(order?.aspirator),
    ontario: normalizeOntarioDraftValue(order?.ontario),
    building: order?.building ?? "",
    client_code: order?.client_code ?? "",
    client_name: order?.client_name ?? "",
    comment: order?.comment ?? "",
    coop: order?.coop ?? "",
    delivery_priority: normalizeLoadingPriorityDraftValue(singleDelivery?.priority),
    delivery_date: toDateInputValue(order?.delivery_date),
    delivery_window_start: toTimeInputValue(singleDelivery?.window_start ?? singleDelivery?.planned_time ?? singleDelivery?.plage_horaire),
    grain_code: order?.grain_code ?? "",
    grain_type: order?.grain_type ?? "",
    is_preloaded_previous_day: normalizePreloadedPreviousDayDraftValue(order?.is_preloaded_previous_day),
    delivery_period: normalizeDeliveryPeriodDraftValue(order?.delivery_period),
    is_dompeur: normalizePreloadedPreviousDayDraftValue(order?.is_dompeur),
    loading_client_name: order?.loading_client_name ?? "",
    loading_priority: normalizeLoadingPriorityDraftValue(order?.loading_priority),
    loading_silo: order?.loading_silo ?? "",
    loading_window_start: toTimeInputValue(order?.loading_window_start ?? order?.plage_horaire ?? order?.window_start_at),
    loading_tonne_rate_override: order?.loading_tonne_rate_override == null ? "" : String(order.loading_tonne_rate_override),
    hourly_rate_override: order?.hourly_rate_override == null ? "" : String(order.hourly_rate_override),
    fixed_rate_override: order?.fixed_rate_override == null ? "" : String(order.fixed_rate_override),
    order_id: order?.order_id ?? "",
    quantity_tons: order?.quantity_tons == null ? "" : String(order.quantity_tons),
    service_time_minutes: order?.service_time_minutes == null ? "" : String(order.service_time_minutes),
    unloading_tonne_rate_override: order?.unloading_tonne_rate_override == null ? "" : String(order.unloading_tonne_rate_override),
    work_order_number: order?.work_order_number ?? "",
  };
}

export function normalizeClientCorrespondenceImportKey(value) {
  const resolvedValue = typeof value === "string" ? value.trim() : String(value || "").trim();
  if (!resolvedValue) {
    return "";
  }

  return resolvedValue
    .replace(/#/g, " number ")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");
}

export function isMissingClientCorrespondenceClientCode(value) {
  return !normalizeOptionalString(value);
}

function normalizeClientCorrespondenceLookupKey(value) {
  return normalizeClientCorrespondenceImportKey(value);
}

export const DATA_MANAGER_COOP_DATALIST_ID = "data-manager-coop-clients";

// A Coop record without Meunerie and without address names who pays, never a place.
export function isCoopOnlyClientCorrespondence(entry) {
  return Boolean(entry?.is_coop) && !entry?.is_meunerie && !normalizeOptionalString(entry?.address);
}

export function buildCoopClientNames(clientCorrespondences) {
  const namesByLookupKey = new Map();
  (Array.isArray(clientCorrespondences) ? clientCorrespondences : []).forEach((entry) => {
    const clientName = entry?.is_coop ? normalizeOptionalString(entry?.client_name) : null;
    const lookupKey = normalizeClientCorrespondenceLookupKey(clientName);
    if (clientName && lookupKey && !namesByLookupKey.has(lookupKey)) {
      namesByLookupKey.set(lookupKey, clientName);
    }
  });
  return [...namesByLookupKey.values()].sort((left, right) => left.localeCompare(right, "fr-CA", { sensitivity: "base" }));
}

export function buildLoadingClientCorrespondenceIndex(clientCorrespondences) {
  const options = [];
  const byLookupKey = {};

  (Array.isArray(clientCorrespondences) ? clientCorrespondences : []).forEach((entry) => {
    if (isCoopOnlyClientCorrespondence(entry)) {
      return;
    }
    const clientName = normalizeOptionalString(entry?.client_name);
    const correspondenceName = normalizeOptionalString(entry?.correspondence_name);
    const value = clientName || correspondenceName;
    if (!value) {
      return;
    }

    const lookupKey = normalizeClientCorrespondenceLookupKey(value);
    if (!lookupKey || byLookupKey[lookupKey]) {
      return;
    }

    const option = {
      address: normalizeOptionalString(entry?.address),
      billTo: normalizeOptionalString(entry?.bill_to),
      correspondenceName,
      lookupKey,
      value,
    };
    [clientName, correspondenceName].forEach((candidateValue) => {
      const candidateLookupKey = normalizeClientCorrespondenceLookupKey(candidateValue);
      if (candidateLookupKey && !byLookupKey[candidateLookupKey]) {
        byLookupKey[candidateLookupKey] = option;
      }
    });
    options.push(option);
  });

  options.sort((left, right) => left.value.localeCompare(right.value, "fr-CA", { sensitivity: "base" }));
  return { byLookupKey, options };
}

function resolveLoadingClientSelection(value, clientCorrespondenceOptionsByLookupKey) {
  const lookupKey = normalizeClientCorrespondenceLookupKey(value);
  return lookupKey ? clientCorrespondenceOptionsByLookupKey[lookupKey] || null : null;
}

// « Facturer à » de la fiche du client livré, pour préremplir une nouvelle commande.
export function resolveClientBillTo(value, clientCorrespondenceOptionsByLookupKey) {
  return resolveLoadingClientSelection(value, clientCorrespondenceOptionsByLookupKey || {})?.billTo ?? null;
}

// Le client livré choisi donne le « Facturer à » de sa fiche, sans écraser un choix fait à la main.
export function buildCreateOrderBillToPatch(field, value, previousClientName, currentBillTo, clientCorrespondenceOptionsByLookupKey) {
  if (field !== "client_name") {
    return {};
  }
  const nextBillTo = resolveClientBillTo(value, clientCorrespondenceOptionsByLookupKey);
  if (!nextBillTo) {
    return {};
  }
  const current = normalizeOptionalString(currentBillTo);
  return !current || current === resolveClientBillTo(previousClientName, clientCorrespondenceOptionsByLookupKey)
    ? { coop: nextBillTo }
    : {};
}

export function coerceLoadingClientDraftValue(value, clientCorrespondenceOptionsByLookupKey) {
  return resolveLoadingClientSelection(value, clientCorrespondenceOptionsByLookupKey)?.value ?? value;
}

export function resolveLoadingClientDraftValueForSave(
  order,
  draft,
  clientCorrespondenceOptionsByLookupKey,
) {
  const draftValue = draft?.loading_client_name ?? "";
  const baselineValue = createEditableOrderDraft(order).loading_client_name;
  return draftValue === baselineValue
    ? draftValue
    : coerceLoadingClientDraftValue(draftValue, clientCorrespondenceOptionsByLookupKey);
}

export const LOADING_CLIENT_SUGGESTIONS_SELECTOR = "[data-loading-client-suggestions='true']";

export function isLoadingClientSuggestionsTarget(target) {
  return target instanceof Element && Boolean(target.closest(LOADING_CLIENT_SUGGESTIONS_SELECTOR));
}

export function getLoadingClientSuggestions(value, clientCorrespondenceOptions) {
  const lookupKey = normalizeClientCorrespondenceLookupKey(value);
  if (lookupKey.length < LOADING_CLIENT_SUGGESTION_MIN_LENGTH) {
    return [];
  }

  return clientCorrespondenceOptions
    .filter((option) => option.lookupKey.includes(lookupKey))
    .slice(0, LOADING_CLIENT_SUGGESTION_LIMIT);
}

export function getProductCodeSuggestions(value, products = []) {
  const prefix = String(value ?? "").trim().toLowerCase();
  if (prefix.length < 3) return [];
  const codes = new Map();
  for (const product of products) {
    const code = String(product.article_number ?? "").trim();
    if (!code.toLowerCase().startsWith(prefix) || codes.has(code)) continue;
    const description = String(product.correspondence ?? "").trim();
    codes.set(code, {
      lookupKey: code,
      value: code,
      label: description && description !== code ? `${code} — ${description}` : code,
    });
  }
  return [...codes.values()].sort((a, b) => a.value.localeCompare(b.value)).slice(0, 8);
}

export function resolveLoadingClientValidationError(value, clientCorrespondenceOptionsByLookupKey) {
  if (!normalizeOptionalString(value)) {
    return "Client de chargement requis.";
  }

  if (!resolveLoadingClientSelection(value, clientCorrespondenceOptionsByLookupKey)) {
    return "Choisir un client existant.";
  }

  return "";
}

export function resolveLoadingAddressPreview(value, clientCorrespondenceOptionsByLookupKey, fallbackAddress = null) {
  return resolveLoadingClientSelection(value, clientCorrespondenceOptionsByLookupKey)?.address
    || normalizeOptionalString(fallbackAddress)
    || null;
}

export function resolveLoadingClientDisplayValue(value, clientCorrespondenceOptionsByLookupKey, fallbackValue = null) {
  return resolveLoadingClientSelection(value, clientCorrespondenceOptionsByLookupKey)?.value
    || normalizeOptionalString(fallbackValue)
    || null;
}

function resolveDeliveryClientSelection(value, clientCorrespondenceOptionsByLookupKey) {
  return resolveLoadingClientSelection(value, clientCorrespondenceOptionsByLookupKey);
}

export function isSelectedClientCanonicalAddress(clientName, address, clientCorrespondenceOptionsByLookupKey) {
  const selectedAddress = normalizeOptionalString(
    resolveDeliveryClientSelection(clientName, clientCorrespondenceOptionsByLookupKey)?.address,
  );
  return Boolean(selectedAddress && selectedAddress === normalizeOptionalString(address));
}

function resolveComparableClientName(value, clientCorrespondenceOptionsByLookupKey) {
  const resolvedValue = resolveLoadingClientSelection(value, clientCorrespondenceOptionsByLookupKey)?.value ?? value;
  return typeof resolvedValue === "string" ? resolvedValue.trim() : "";
}

function hasOptionalTonneRateDraftValue(value) {
  return typeof value === "string" && value.trim() !== "";
}

function appendTonneRateClearOnClientChange(
  patch,
  {
    clientCorrespondenceOptionsByLookupKey,
    currentClientValue,
    nextClientValue,
    sourceDraft,
    tonneRateField,
  },
) {
  if (!hasOptionalTonneRateDraftValue(sourceDraft?.[tonneRateField])) {
    return;
  }

  if (
    resolveComparableClientName(currentClientValue, clientCorrespondenceOptionsByLookupKey)
    !== resolveComparableClientName(nextClientValue, clientCorrespondenceOptionsByLookupKey)
  ) {
    patch[tonneRateField] = "";
  }
}

export function buildCreateDeliveryDraftPatch(field, value, deliveryDraft, clientCorrespondenceOptionsByLookupKey) {
  if (field !== "client_name") {
    return { [field]: value };
  }

  const currentSelection = resolveDeliveryClientSelection(deliveryDraft?.client_name, clientCorrespondenceOptionsByLookupKey);
  const nextSelection = resolveDeliveryClientSelection(value, clientCorrespondenceOptionsByLookupKey);
  const nextClientName = nextSelection?.value ?? value;
  const nextPatch = {
    client_name: nextClientName,
  };
  appendTonneRateClearOnClientChange(nextPatch, {
    clientCorrespondenceOptionsByLookupKey,
    currentClientValue: deliveryDraft?.client_name,
    nextClientValue: nextClientName,
    sourceDraft: deliveryDraft,
    tonneRateField: "unloading_tonne_rate_override",
  });
  const nextAddress = normalizeOptionalString(nextSelection?.address);

  if (nextAddress) {
    if (nextAddress !== normalizeOptionalString(deliveryDraft?.address)) {
      nextPatch.address = nextAddress;
    }
    return nextPatch;
  }

  const currentSelectionAddress = normalizeOptionalString(currentSelection?.address);
  const currentAddress = normalizeOptionalString(deliveryDraft?.address);
  if (nextSelection && currentSelectionAddress && currentAddress === currentSelectionAddress) {
    nextPatch.address = "";
  }

  return nextPatch;
}

export function buildDeliveryClientDraftPatch(field, value, draft, clientCorrespondenceOptionsByLookupKey) {
  if ((field === "delivery_date" || field === "order_date") && typeof value === "string") {
    return { [field]: parseDisplayDateInputValue(value) };
  }

  if (field === "loading_client_name") {
    const nextPatch = { loading_client_name: value };
    appendTonneRateClearOnClientChange(nextPatch, {
      clientCorrespondenceOptionsByLookupKey,
      currentClientValue: draft?.loading_client_name,
      nextClientValue: value,
      sourceDraft: draft,
      tonneRateField: "loading_tonne_rate_override",
    });
    return nextPatch;
  }

  if (field !== "client_name") {
    return { [field]: value };
  }

  const currentSelection = resolveDeliveryClientSelection(draft?.client_name, clientCorrespondenceOptionsByLookupKey);
  const nextSelection = resolveDeliveryClientSelection(value, clientCorrespondenceOptionsByLookupKey);
  const nextClientName = nextSelection?.value ?? value;
  const nextPatch = {
    client_name: nextClientName,
  };
  appendTonneRateClearOnClientChange(nextPatch, {
    clientCorrespondenceOptionsByLookupKey,
    currentClientValue: draft?.client_name,
    nextClientValue: nextClientName,
    sourceDraft: draft,
    tonneRateField: "unloading_tonne_rate_override",
  });
  const nextAddress = normalizeOptionalString(nextSelection?.address);

  if (nextAddress) {
    if (nextAddress !== normalizeOptionalString(draft?.address)) {
      nextPatch.address = nextAddress;
    }
    return nextPatch;
  }

  const currentSelectionAddress = normalizeOptionalString(currentSelection?.address);
  const currentAddress = normalizeOptionalString(draft?.address);
  if (nextSelection && currentSelectionAddress && currentAddress === currentSelectionAddress) {
    nextPatch.address = "";
  }

  return nextPatch;
}

export function normalizeAspiratorDraftValue(value) {
  const resolvedValue = normalizeOptionalString(value);
  if (resolvedValue === "oui" || resolvedValue === "non") {
    return resolvedValue;
  }

  return "";
}

export function serializeAspiratorValue(value) {
  return normalizeAspiratorDraftValue(value) || null;
}

export function normalizeOntarioDraftValue(value) {
  const resolvedValue = normalizeOptionalString(value);
  return resolvedValue === "oui" ? "oui" : "non";
}

export function serializeOntarioValue(value) {
  return normalizeOntarioDraftValue(value);
}

export function normalizePreloadedPreviousDayDraftValue(value) {
  if (value === true) {
    return "oui";
  }

  if (value === false) {
    return "non";
  }

  return normalizeOptionalString(value) === "oui" ? "oui" : "non";
}

export function serializePreloadedPreviousDayValue(value) {
  return normalizePreloadedPreviousDayDraftValue(value) === "oui";
}

export function normalizeDeliveryPeriodDraftValue(value) {
  const resolvedValue = normalizeOptionalString(value);
  if (resolvedValue === "evening" || resolvedValue === "soir" || resolvedValue === "oui") {
    return "oui";
  }

  return "non";
}

export function serializeDeliveryPeriodValue(value) {
  return normalizeDeliveryPeriodDraftValue(value) === "oui" ? "evening" : "day";
}

export function normalizeLoadingPriorityDraftValue(value) {
  const resolvedValue = normalizeOptionalString(value);
  const normalizedValue = resolvedValue ? resolvedValue.toUpperCase() : "";
  if (normalizedValue === "P1" || normalizedValue === "P2" || normalizedValue === "P3") {
    return normalizedValue;
  }

  return "";
}

export function serializeLoadingPriorityValue(value) {
  return normalizeLoadingPriorityDraftValue(value) || null;
}

export function normalizeRequiredNumber(value) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

export function normalizeOptionalTonneRate(value) {
  if (value === null || value === undefined || (typeof value === "string" && value.trim() === "")) {
    return null;
  }

  const normalizedValue = typeof value === "string" ? value.trim().replace(",", ".") : value;
  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : null;
}

export function isValidOptionalTonneRate(value) {
  if (value === null || value === undefined || (typeof value === "string" && value.trim() === "")) {
    return true;
  }

  const normalizedValue = String(value).trim().replace(",", ".");
  return /^\d+(?:\.\d{1,4})?$/.test(normalizedValue)
    && normalizeOptionalTonneRate(normalizedValue) !== null;
}

export function normalizeOptionalInteger(value) {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "string" && value.trim() === "") {
    return null;
  }

  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue) || !Number.isInteger(parsedValue) || parsedValue < 0) {
    return null;
  }

  return parsedValue;
}

export function formatDateInput(date) {
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function normalizeDateDraftInputValue(value) {
  if (typeof value !== "string") {
    return "";
  }

  const digits = value.trim().replace(/\D/g, "").slice(0, 8);
  if (!digits) {
    return "";
  }

  if (digits.length <= 4) {
    return digits;
  }

  if (digits.length <= 6) {
    return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  }

  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
}

export function isCompleteDateDraftInputValue(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(normalizeDateDraftInputValue(value));
}

export function resolveOrderBrowserDeliveryDate({
  currentDispatchDates = [],
  isBrowseMode = false,
  orderBrowseDateFilter = "",
} = {}) {
  const normalizedBrowseDateFilter = typeof orderBrowseDateFilter === "string"
    ? orderBrowseDateFilter.trim()
    : "";

  if (normalizedBrowseDateFilter) {
    return normalizedBrowseDateFilter;
  }

  if (isBrowseMode) {
    return "";
  }

  if (Array.isArray(currentDispatchDates) && currentDispatchDates.length === 1) {
    return currentDispatchDates[0];
  }

  return "";
}

export function toDateInputValue(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    const match = trimmedValue.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) {
      return match[1];
    }
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.valueOf()) ? "" : formatDateInput(parsedDate);
}

export function toTimeInputValue(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    const plainTimeMatch = trimmedValue.match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
    if (plainTimeMatch) {
      return `${plainTimeMatch[1]}:${plainTimeMatch[2]}`;
    }

    const embeddedTimeMatch = trimmedValue.match(/[T\s](\d{2}):(\d{2})(?::\d{2})?/);
    if (embeddedTimeMatch) {
      return `${embeddedTimeMatch[1]}:${embeddedTimeMatch[2]}`;
    }
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.valueOf())) {
    return "";
  }

  return `${String(parsedDate.getHours()).padStart(2, "0")}:${String(parsedDate.getMinutes()).padStart(2, "0")}`;
}

export function formatDateDisplay(value) {
  if (!value) {
    return "—";
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    const match = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);
    if (match) {
      const [, year, month, day, hours, minutes] = match;
      return hours && minutes ? `${day}-${month}-${year} ${hours}:${minutes}` : `${day}-${month}-${year}`;
    }

    return trimmedValue || "—";
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.valueOf())) {
    return "—";
  }

  return `${String(parsedDate.getDate()).padStart(2, "0")}-${String(parsedDate.getMonth() + 1).padStart(2, "0")}-${parsedDate.getFullYear()}`;
}

export function toDisplayDateInputValue(value) {
  const isoValue = toDateInputValue(value);
  if (!isoValue) {
    return "";
  }

  const [, year, month, day] = isoValue.match(/^(\d{4})-(\d{2})-(\d{2})$/) || [];
  if (!year || !month || !day) {
    return "";
  }

  return `${day}-${month}-${year}`;
}

export function maskDisplayDateInputValue(value) {
  const text = typeof value === "string" ? value.trim() : "";
  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  }
  // Preserve segment boundaries while replacing or deleting a day/month.
  if (/^\d{0,2}-\d{0,2}-\d{0,4}$/.test(text)) {
    return text;
  }
  const displayMatch = text.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (displayMatch) {
    return `${displayMatch[1].padStart(2, "0")}-${displayMatch[2].padStart(2, "0")}-${displayMatch[3]}`;
  }
  const digits = text.replace(/\D/g, "").slice(0, 8);
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter(Boolean).join("-");
}

export function parseDisplayDateInputValue(value) {
  if (typeof value !== "string") {
    return "";
  }

  const trimmedValue = value.trim();
  const isoMatch = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const displayMatch = isoMatch
    ? [trimmedValue, isoMatch[3], isoMatch[2], isoMatch[1]]
    : trimmedValue.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/)
      || trimmedValue.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (displayMatch) {
    const [, day, month, year] = displayMatch;
    const dayNumber = Number(day);
    const monthNumber = Number(month);
    const date = new Date(0);
    date.setUTCFullYear(Number(year), monthNumber - 1, dayNumber);
    if (Number(year) > 0 && date.getUTCFullYear() === Number(year)
      && date.getUTCMonth() === monthNumber - 1 && date.getUTCDate() === dayNumber) {
      return `${year}-${String(monthNumber).padStart(2, "0")}-${String(dayNumber).padStart(2, "0")}`;
    }
    return "";
  }

  return "";
}

export function formatWindowDisplay(value) {
  if (!value) {
    return "—";
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    const timeMatch = trimmedValue.match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
    if (timeMatch) {
      return `${timeMatch[1]}:${timeMatch[2]}`;
    }

    const embeddedTimeMatch = trimmedValue.match(/[T\s](\d{2}):(\d{2})(?::\d{2})?/);
    if (embeddedTimeMatch) {
      return `${embeddedTimeMatch[1]}:${embeddedTimeMatch[2]}`;
    }

    return trimmedValue || "—";
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.valueOf())) {
    return "—";
  }

  return `${String(parsedDate.getHours()).padStart(2, "0")}:${String(parsedDate.getMinutes()).padStart(2, "0")}`;
}

export function resolveOrderTimeFieldValue(order, fieldKey) {
  if (fieldKey === "loading_window_start") {
    return order?.loading_window_start ?? order?.plage_horaire ?? order?.window_start_at;
  }

  if (fieldKey === "delivery_window_start") {
    const delivery = resolveEditableSingleDelivery(order);
    return delivery?.window_start ?? delivery?.planned_time ?? delivery?.plage_horaire;
  }

  return order?.[fieldKey];
}

export function resolveOrderDeliveryPriorityValue(order) {
  return resolveEditableSingleDelivery(order)?.priority ?? null;
}

export function formatQuantityTons(value) {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return "—";
  }

  return new Intl.NumberFormat("fr-CA", {
    maximumFractionDigits: 2,
    minimumFractionDigits: Number.isInteger(parsedValue) ? 0 : 1,
  }).format(parsedValue);
}

export function formatTonneRate(value) {
  if (value === null || value === undefined || (typeof value === "string" && value.trim() === "")) {
    return "—";
  }
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return "—";
  }

  const formattedValue = new Intl.NumberFormat("fr-CA", {
    maximumFractionDigits: 4,
    minimumFractionDigits: Number.isInteger(parsedValue) ? 0 : 1,
  }).format(parsedValue);

  return `${formattedValue} $/t`;
}

export function formatTextDisplay(value) {
  if (typeof value !== "string") {
    return value == null ? "—" : String(value);
  }

  const trimmedValue = value.trim();
  return trimmedValue || "—";
}

export function resolveBillingApprovalMeta(order) {
  const status = typeof order?.billing_approval_status === "string"
    ? order.billing_approval_status.trim().toLowerCase()
    : "not_ready";

  if (status === "approved") {
    const approver = normalizeOptionalString(order?.billing_approved_by_email_snapshot)
      || normalizeOptionalString(order?.billing_approved_by_user_id);
    const approvedAt = order?.billing_approved_at ? new Date(order.billing_approved_at) : null;
    const approvedAtLabel = approvedAt && !Number.isNaN(approvedAt.valueOf())
      ? new Intl.DateTimeFormat("fr-CA", { dateStyle: "medium", timeStyle: "short" }).format(approvedAt)
      : "";
    const titleParts = [
      approver ? `Approuvée par ${approver}` : "Approuvée pour facturation",
      approvedAtLabel,
    ].filter(Boolean);

    return {
      className: "border border-emerald-200 bg-emerald-50 text-emerald-700",
      label: "Approuvée",
      status,
      title: titleParts.join(" · "),
    };
  }

  if (status === "pending") {
    return {
      className: "border border-amber-200 bg-amber-50 text-amber-700",
      label: "À approuver",
      status,
      title: "Livrée et en attente d’approbation pour facturation",
    };
  }

  return {
    className: "border border-slate-200 bg-white text-slate-400",
    label: "—",
    status: "not_ready",
    title: "La commande doit être livrée avant de pouvoir être approuvée",
  };
}

export function formatOrderActionDisplay(value) {
  if (typeof value !== "string") {
    return "—";
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return "—";
  }

  const normalizedValue = trimmedValue.toLowerCase();
  if (normalizedValue === "chargement" || normalizedValue === "loading") {
    return "Chargement";
  }
  if (normalizedValue === "déchargement" || normalizedValue === "dechargement" || normalizedValue === "delivery" || normalizedValue === "unloading") {
    return "Déchargement";
  }

  return trimmedValue;
}

function formatStatusTokenLabel(status) {
  const normalizedStatus = typeof status === "string" ? status.trim().toLowerCase() : "";
  return normalizedStatus ? normalizedStatus.replace(/[_-]+/g, " ") : "—";
}

export function resolveDispatchStatusMeta(status) {
  const normalizedStatus = typeof status === "string" ? status.trim().toLowerCase() : "";
  return DISPATCH_STATUS_META[normalizedStatus] || {
    label: formatStatusTokenLabel(normalizedStatus),
    className: "border border-slate-200 bg-slate-100 text-slate-700",
  };
}

function hasOrderExecutionValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

export function isOrderBillingApproved(order) {
  return normalizeDispatchStatusToken(order?.billing_approval_status) === "approved";
}

export function isOrderExecutionRelevant(order, { canWriteExecution = false } = {}) {
  if (!order?.order_id) {
    return false;
  }

  if (isOrderBillingApproved(order)) {
    return false;
  }

  if (canWriteExecution && !order?.is_cancelled) {
    return true;
  }

  const dispatchStatus = normalizeDispatchStatusToken(order?.dispatch_status);
  const approvalStatus = normalizeDispatchStatusToken(order?.billing_approval_status);
  if (dispatchStatus === "completed" || approvalStatus === "pending") {
    return true;
  }

  if (
    hasOrderExecutionValue(order?.actual_loaded_quantity_tons)
    || hasOrderExecutionValue(order?.actual_delivered_quantity_tons)
    || hasOrderExecutionValue(order?.delivered_by_driver_name)
  ) {
    return true;
  }

  return (Array.isArray(order?.deliveries) ? order.deliveries : []).some((delivery) => (
    hasOrderExecutionValue(delivery?.actual_delivered_quantity_tons)
    || hasOrderExecutionValue(delivery?.delivered_by_driver_name)
    || normalizeDispatchStatusToken(delivery?.driver_status) === "delivered"
  ));
}

export function resolveOrderPrimaryAction({
  canApproveForBilling = false,
  canOpenExecution = false,
} = {}) {
  if (canApproveForBilling) {
    return "approve";
  }
  if (canOpenExecution) {
    return "execution";
  }
  return null;
}

export function resolveDeliveryMeta(order) {
  const dispatchStatus = normalizeDispatchStatusToken(order?.dispatch_status);
  const dispatchBackendStatus = normalizeDispatchStatusToken(order?.dispatch_backend_status);

  if (dispatchStatus === "completed" || dispatchBackendStatus === "livre") {
    return {
      className: "border border-sky-200 bg-sky-50 text-sky-700",
      label: "Livrée",
      title: "Commande livrée.",
    };
  }

  return {
    className: "border border-slate-200 bg-slate-100 text-slate-500",
    label: "—",
    title: "Livraison non confirmée.",
  };
}

export function resolveLockMeta(order) {
  if (order?.is_cancelled) {
    return {
      className: "border border-rose-200 bg-rose-50 text-rose-700",
      label: "Annulée",
      title: order?.lock_reason || "Commande annulée.",
    };
  }

  const dispatchStatus = normalizeDispatchStatusToken(order?.dispatch_status);
  const dispatchDayState = normalizeDispatchStatusToken(order?.dispatch_day_state);
  const dispatchBackendStatus = normalizeDispatchStatusToken(order?.dispatch_backend_status);
  const isDelivered = dispatchStatus === "completed" || dispatchBackendStatus === "livre";

  if (!isDelivered && (dispatchDayState === "closed" || dispatchBackendStatus === "cloture")) {
    return {
      className: "border border-violet-200 bg-violet-50 text-violet-700",
      label: "Fermée",
      title: order?.lock_reason || "Journée de dispatch clôturée.",
    };
  }

  if (order?.lock_reason || order?.is_editable === false) {
    return {
      className: "border border-amber-200 bg-amber-50 text-amber-700",
      label: "Verrouillée",
      title: order?.lock_reason || "Commande en lecture seule.",
    };
  }

  return {
    className: "border border-emerald-200 bg-emerald-50 text-emerald-700",
    label: "Ouverte",
    title: "Commande modifiable.",
  };
}

export function buildCreatePayload(draft) {
  return buildCreatePayloads(draft)[0] || {};
}

function buildSingleCreatePayload(draft, delivery) {
  return {
    ...(typeof delivery.allow_missing_civic_number === "boolean" ? { allow_missing_civic_number: delivery.allow_missing_civic_number } : {}),
    address: normalizeOptionalString(delivery.address),
    aspirator: serializeAspiratorValue(delivery.aspirator),
    ontario: serializeOntarioValue(delivery.ontario),
    building: normalizeOptionalString(delivery.building),
    client_code: normalizeOptionalString(draft.client_code),
    client_name: normalizeOptionalString(delivery.client_name),
    delivery_date: normalizeDateDraftInputValue(draft.delivery_date),
    grain_code: normalizeOptionalString(delivery.grain_code),
    comment: normalizeOptionalString(delivery.comment),
    coop: normalizeOptionalString(draft.coop),
    delivery_period: serializeDeliveryPeriodValue(draft.delivery_period),
    grain_type: normalizeOptionalString(delivery.grain_type),
    is_preloaded_previous_day: serializePreloadedPreviousDayValue(draft.is_preloaded_previous_day),
    is_dompeur: serializePreloadedPreviousDayValue(draft.is_dompeur),
    loading_client_name: normalizeOptionalString(draft.loading_client_name),
    loading_priority: serializeLoadingPriorityValue(delivery.loading_priority),
    loading_silo: normalizeOptionalString(delivery.loading_silo),
    loading_window_start: normalizeOptionalString(delivery.loading_window_start),
    loading_tonne_rate_override: normalizeOptionalTonneRate(draft.loading_tonne_rate_override),
    hourly_rate_override: normalizeOptionalTonneRate(draft.hourly_rate_override),
    fixed_rate_override: normalizeOptionalTonneRate(draft.fixed_rate_override),
    order_date: normalizeDateDraftInputValue(draft.order_date) || formatDateInput(new Date()),
    deliveries: [{
      address: normalizeOptionalString(delivery.address),
      ...(typeof delivery.allow_missing_civic_number === "boolean" ? { allow_missing_civic_number: delivery.allow_missing_civic_number } : {}),
      client_name: normalizeOptionalString(delivery.client_name),
      priority: serializeLoadingPriorityValue(delivery.delivery_priority),
      quantity_tons: normalizeRequiredNumber(delivery.quantity_tons),
      window_start: normalizeOptionalString(delivery.delivery_window_start),
    }],
    quantity_tons: normalizeRequiredNumber(delivery.quantity_tons),
    work_order_number: normalizeOptionalString(delivery.work_order_number),
  };
}

export function buildCreatePayloads(draft) {
  return getCreateOrderDeliveries(draft).map((delivery) => buildSingleCreatePayload(draft, delivery));
}

function buildNormalizedUpdatePayload(order, draft) {
  const payload = {
    aspirator: serializeAspiratorValue(draft.aspirator),
    ontario: serializeOntarioValue(draft.ontario),
    building: normalizeOptionalString(draft.building),
    client_code: normalizeOptionalString(draft.client_code),
    client_name: normalizeOptionalString(draft.client_name),
    delivery_date: draft.delivery_date,
    grain_code: normalizeOptionalString(draft.grain_code),
    comment: normalizeOptionalString(draft.comment),
    coop: normalizeOptionalString(draft.coop),
    delivery_period: serializeDeliveryPeriodValue(draft.delivery_period),
    grain_type: normalizeOptionalString(draft.grain_type),
    is_preloaded_previous_day: serializePreloadedPreviousDayValue(draft.is_preloaded_previous_day),
    is_dompeur: serializePreloadedPreviousDayValue(draft.is_dompeur),
    loading_client_name: normalizeOptionalString(draft.loading_client_name),
    loading_priority: serializeLoadingPriorityValue(draft.loading_priority),
    loading_silo: normalizeOptionalString(draft.loading_silo),
    loading_window_start: normalizeOptionalString(draft.loading_window_start),
    loading_tonne_rate_override: normalizeOptionalTonneRate(draft.loading_tonne_rate_override),
    hourly_rate_override: normalizeOptionalTonneRate(draft.hourly_rate_override),
    fixed_rate_override: normalizeOptionalTonneRate(draft.fixed_rate_override),
    quantity_tons: normalizeRequiredNumber(draft.quantity_tons),
    service_time_minutes: normalizeOptionalInteger(draft.service_time_minutes),
    work_order_number: normalizeOptionalString(draft.work_order_number),
  };

  // Only send address when the user actually changed it. The draft hydrates
  // address from the delivery when order.address is empty; always sending it
  // would backfill the order and pollute audit ("Adresse: — → …").
  if (hasOrderAddressFieldChanged(order, draft, "address")) {
    payload.address = normalizeOptionalString(draft.address);
    if (typeof draft.allow_missing_civic_number === "boolean") payload.allow_missing_civic_number = draft.allow_missing_civic_number;
  }

  const deliveryPayload = buildSingleDeliveryWindowPayload(order, draft);
  if (deliveryPayload) {
    payload.deliveries = deliveryPayload;
  }

  return payload;
}

function buildExpectedOrderSnapshot(order) {
  return buildNormalizedUpdatePayload(order, createEditableOrderDraft(order));
}

export function buildUpdatePayload(order, draft) {
  const payload = buildNormalizedUpdatePayload(order, draft);

  if (order) {
    payload.expected_order_snapshot = buildExpectedOrderSnapshot(order);
  }

  if (order?.updated_at) {
    payload.expected_updated_at = order.updated_at;
  }

  return payload;
}

export function buildDeletePayload(order) {
  return order?.updated_at
    ? { expected_updated_at: order.updated_at }
    : {};
}

function buildSingleDeliveryWindowPayload(order, draft) {
  if (order && !canEditSingleDeliveryWindowFields(order)) {
    return undefined;
  }

  const singleDelivery = resolveEditableSingleDelivery(order);
  const baselineDraft = createEditableOrderDraft(order);
  const payload = {
    priority: serializeLoadingPriorityValue(draft.delivery_priority),
    window_start: normalizeOptionalString(draft.delivery_window_start),
  };
  const deliveryId = normalizeOptionalString(singleDelivery?.delivery_id);
  if (deliveryId) {
    payload.delivery_id = deliveryId;
  }

  if (normalizeOptionalString(draft.address) !== normalizeOptionalString(baselineDraft.address)) {
    payload.address = normalizeOptionalString(draft.address);
    if (typeof draft.allow_missing_civic_number === "boolean") payload.allow_missing_civic_number = draft.allow_missing_civic_number;
  }

  return [payload];
}

export function getOrderDraftErrors(draft, { clientCorrespondenceOptionsByLookupKey = {}, includeOrderId = false } = {}) {
  const errors = {};
  const loadingWindowStart = normalizeOptionalString(draft.loading_window_start);
  const loadingPriority = normalizeLoadingPriorityDraftValue(draft.loading_priority);
  const deliveryWindowStart = normalizeOptionalString(draft.delivery_window_start);
  const deliveryPriority = normalizeLoadingPriorityDraftValue(draft.delivery_priority);
  const isPreloadedPreviousDay = serializePreloadedPreviousDayValue(draft.is_preloaded_previous_day);

  if (draft.aspirator !== "" && draft.aspirator !== "oui" && draft.aspirator !== "non") {
    errors.aspirator = "Aspirateur doit valoir Oui, Non ou Vide.";
  }

  if (draft.ontario !== "oui" && draft.ontario !== "non") {
    errors.ontario = "Ontario doit valoir Oui ou Non.";
  }

  if (includeOrderId && !normalizeOptionalString(draft.order_id)) {
    errors.order_id = "Identifiant requis.";
  }

  if (typeof draft.delivery_date !== "string" || draft.delivery_date.trim().length === 0) {
    errors.delivery_date = "Date requise.";
  }

  const loadingClientError = resolveLoadingClientValidationError(draft.loading_client_name, clientCorrespondenceOptionsByLookupKey);
  if (loadingClientError) {
    errors.loading_client_name = loadingClientError;
  }

  if (isPreloadedPreviousDay && loadingWindowStart) {
    errors.loading_window_start = "Début chargement interdit quand Chargé la veille vaut Oui.";
  }

  if (!isPreloadedPreviousDay && loadingWindowStart && !loadingPriority) {
    errors.loading_priority = "Priorité chargement requise si un début chargement est saisi.";
  }

  if (deliveryWindowStart && !deliveryPriority) {
    errors.delivery_priority = "Priorité déchargement requise si un début déchargement est saisi.";
  }

  const quantityValue = normalizeRequiredNumber(draft.quantity_tons);
  if (quantityValue === null || quantityValue <= 0) {
    errors.quantity_tons = "Quantité supérieure à 0 requise.";
  }

  if (typeof draft.service_time_minutes === "string" && draft.service_time_minutes.trim().length > 0) {
    const serviceTimeValue = normalizeOptionalInteger(draft.service_time_minutes);
    if (serviceTimeValue === null) {
      errors.service_time_minutes = "Service en minutes entières, supérieur ou égal à 0.";
    }
  }

  if (!isValidOptionalTonneRate(draft.loading_tonne_rate_override)) {
    errors.loading_tonne_rate_override = "Exception $/t invalide (maximum 4 décimales).";
  }

  if (!isValidOptionalTonneRate(draft.hourly_rate_override)) {
    errors.hourly_rate_override = "Exception $/h invalide (maximum 4 décimales).";
  }

  if (!isValidOptionalTonneRate(draft.fixed_rate_override)) {
    errors.fixed_rate_override = "Exception $ fixe invalide (maximum 4 décimales).";
  }

  return errors;
}

export function getNewOrderDraftErrors(draft, clientCorrespondenceOptionsByLookupKey) {
  const errors = {};
  if (!normalizeOptionalString(draft.coop)) {
    errors.coop = "« Facturer à » requis : choisissez l’entreprise facturée, ou « aucun ».";
  }
  const deliveries = getCreateOrderDeliveries(draft);
  const isPreloadedPreviousDay = serializePreloadedPreviousDayValue(draft.is_preloaded_previous_day);

  if (!isCompleteDateDraftInputValue(draft.delivery_date)) {
    errors.delivery_date = normalizeOptionalString(draft.delivery_date)
      ? "Date invalide."
      : "Date requise.";
  }

  const loadingClientError = resolveLoadingClientValidationError(draft.loading_client_name, clientCorrespondenceOptionsByLookupKey);
  if (loadingClientError) {
    errors.loading_client_name = loadingClientError;
  }

  if (!isValidOptionalTonneRate(draft.loading_tonne_rate_override)) {
    errors.loading_tonne_rate_override = "Exception $/t invalide (maximum 4 décimales).";
  }

  if (!isValidOptionalTonneRate(draft.hourly_rate_override)) {
    errors.hourly_rate_override = "Exception $/h invalide (maximum 4 décimales).";
  }

  if (!isValidOptionalTonneRate(draft.fixed_rate_override)) {
    errors.fixed_rate_override = "Exception $ fixe invalide (maximum 4 décimales).";
  }

  deliveries.forEach((delivery, index) => {
    const loadingWindowStart = normalizeOptionalString(delivery.loading_window_start);
    const loadingPriority = normalizeLoadingPriorityDraftValue(delivery.loading_priority);

    if (!normalizeOptionalString(delivery.client_name)) {
      errors[`deliveries.${index}.client_name`] = "Client requis.";
    }

    if (!normalizeOptionalString(delivery.address)) {
      errors[`deliveries.${index}.address`] = "Adresse de déchargement requise.";
    }

    if (isPreloadedPreviousDay && loadingWindowStart) {
      errors[`deliveries.${index}.loading_window_start`] = "Début chargement interdit quand Chargé la veille vaut Oui.";
    }

    if (!isPreloadedPreviousDay && loadingWindowStart && !loadingPriority) {
      errors[`deliveries.${index}.loading_priority`] = "Priorité chargement requise si un début chargement est saisi.";
    }

    if (delivery.aspirator !== "" && delivery.aspirator !== "oui" && delivery.aspirator !== "non") {
      errors[`deliveries.${index}.aspirator`] = "Aspirateur doit valoir Oui, Non ou Vide.";
    }

    if (delivery.ontario !== "oui" && delivery.ontario !== "non") {
      errors[`deliveries.${index}.ontario`] = "Ontario doit valoir Oui ou Non.";
    }

    if (normalizeOptionalString(delivery.delivery_window_start) && !normalizeLoadingPriorityDraftValue(delivery.delivery_priority)) {
      errors[`deliveries.${index}.delivery_priority`] = "Priorité déchargement requise si un début déchargement est saisi.";
    }

    const quantityValue = normalizeRequiredNumber(delivery.quantity_tons);
    if (quantityValue === null || quantityValue <= 0) {
      errors[`deliveries.${index}.quantity_tons`] = "Quantité supérieure à 0 requise.";
    }

    if (!normalizeOptionalString(delivery.grain_type)) {
      errors[`deliveries.${index}.grain_type`] = "Produit requis.";
    }

    if (!normalizeOptionalString(delivery.grain_code)) {
      errors[`deliveries.${index}.grain_code`] = "Code produit requis.";
    }
  });

  return errors;
}

export function hasDraftErrors(errors) {
  return Object.keys(errors).length > 0;
}

export function resolveOrderSaveDisabledReason({
  draft = null,
  errors = {},
  isPending = false,
  isValidatingAddress = false,
  order = null,
} = {}) {
  if (isValidatingAddress) {
    return "Validation de l'adresse en cours.";
  }

  if (isPending) {
    return "Enregistrement en cours.";
  }

  if (hasDraftErrors(errors)) {
    return Object.values(errors).filter(Boolean).join(" ");
  }

  if (order && draft && !hasOrderDraftChanges(order, draft)) {
    return "Aucune modification détectée.";
  }

  return "";
}

export function resolveCreateOrderDisabledReason({
  errors = {},
  isPending = false,
  isValidatingAddress = false,
} = {}) {
  return resolveCreateOrderDisabledMessages({
    errors,
    isPending,
    isValidatingAddress,
  }).join(" ");
}

export function resolveCreateOrderDisabledMessages({
  errors = {},
  isPending = false,
  isValidatingAddress = false,
} = {}) {
  if (isValidatingAddress) {
    return ["Validation de l'adresse en cours."];
  }

  if (isPending) {
    return ["Sauvegarde en cours."];
  }

  return Object.values(errors).filter(Boolean);
}

export function hasOrderAddressFieldChanged(order, draft, fieldKey) {
  const baselineDraft = createEditableOrderDraft(order);
  return normalizeOptionalString(draft?.[fieldKey]) !== normalizeOptionalString(baselineDraft?.[fieldKey]);
}

export function extractAddressStreetText(value) {
  const normalized = normalizeOptionalString(value);
  if (!normalized) {
    return "";
  }

  const parts = normalized.split(/[,;]/).map((part) => part.trim()).filter(Boolean);
  if (!parts.length) {
    return normalized;
  }

  if (/^\d+$/.test(parts[0]) && parts.length > 1) {
    return `${parts[0]} ${parts[1]}`;
  }

  return parts[0];
}

export function addressHasCivicNumber(value) {
  const streetText = extractAddressStreetText(value);
  if (!streetText) {
    return false;
  }

  return streetText
    .split(/\s+/)
    .some((token) => /^\d+$/.test(token));
}

export class MissingCivicNumberConfirmationCancelledError extends Error {
  constructor() {
    super("Validation annulée : numéro civique manquant.");
    this.name = "MissingCivicNumberConfirmationCancelledError";
  }
}

export function isMissingCivicNumberConfirmationCancelledError(error) {
  return error instanceof MissingCivicNumberConfirmationCancelledError
    || (error instanceof Error && error.name === "MissingCivicNumberConfirmationCancelledError");
}

export function isPointerInsideConfirmationModal(target) {
  if (!(target instanceof Node)) {
    return false;
  }

  return Boolean(target.closest('[data-react-confirmation-modal="true"]'));
}

export function resolveAddressValidationFailureMessage(fieldLabel, validationResult) {
  if (!validationResult) {
    return `${fieldLabel} invalide ou introuvable.`;
  }

  if (validationResult.status !== "success") {
    return normalizeOptionalString(validationResult.error_message) || `${fieldLabel} invalide ou introuvable.`;
  }

  if (!validationResult.coordinates_available) {
    return normalizeOptionalString(validationResult.error_message) || `${fieldLabel} introuvable ou non routable.`;
  }

  if (!normalizeOptionalString(validationResult.resolved_address)) {
    return `${fieldLabel} introuvable ou non routable.`;
  }

  return "";
}

export function buildAddressValidationToast({ fieldLabel, inputAddress, resolvedAddress, validationResult }) {
  const normalizedInputAddress = normalizeOptionalString(inputAddress) || "Adresse non fournie.";
  const normalizedResolvedAddress = normalizeOptionalString(resolvedAddress);
  const isSuccess = validationResult?.status === "success";

  if (isSuccess) {
    return {
      type: "success",
      title: `${fieldLabel} validée`,
      message: normalizedResolvedAddress && normalizedResolvedAddress !== normalizedInputAddress
        ? `Saisie: ${normalizedInputAddress}. Résolution retenue: ${normalizedResolvedAddress}.`
        : `Adresse confirmée: ${normalizedInputAddress}.`,
    };
  }

  const errorMessage = normalizeOptionalString(validationResult?.error_message) || `${fieldLabel} invalide ou introuvable.`;
  return {
    type: "error",
    title: `${fieldLabel} à vérifier`,
    message: normalizedResolvedAddress
      ? `Saisie: ${normalizedInputAddress}. Correspondance rejetée: ${normalizedResolvedAddress}. ${errorMessage}`
      : `Saisie: ${normalizedInputAddress}. ${errorMessage}`,
  };
}

function clampNumber(value, minValue, maxValue) {
  return Math.min(Math.max(value, minValue), maxValue);
}

export function resolveAddressValidationToastPositionStyle(anchorElement) {
  if (!(anchorElement instanceof HTMLElement)) {
    return undefined;
  }

  const rect = anchorElement.getBoundingClientRect();
  if (!rect.width && !rect.height) {
    return undefined;
  }

  const viewportWidth = window.innerWidth;
  const maxLeft = Math.max(
    ANCHORED_ADDRESS_TOAST_MARGIN_PX,
    viewportWidth - ANCHORED_ADDRESS_TOAST_ESTIMATED_WIDTH_PX - ANCHORED_ADDRESS_TOAST_MARGIN_PX,
  );
  const left = clampNumber(rect.left, ANCHORED_ADDRESS_TOAST_MARGIN_PX, maxLeft);
  const anchoredTop = Math.max(
    ANCHORED_ADDRESS_TOAST_MARGIN_PX + ANCHORED_ADDRESS_TOAST_ESTIMATED_HEIGHT_PX,
    rect.top - ANCHORED_ADDRESS_TOAST_MARGIN_PX,
  );

  return {
    left: `${Math.round(left)}px`,
    top: `${Math.round(anchoredTop)}px`,
    transform: "translateY(-100%)",
  };
}

export function hasOrderDraftChanges(order, draft) {
  const baselineDraft = createEditableOrderDraft(order);
  return (
    normalizeOptionalString(draft.address) !== normalizeOptionalString(baselineDraft.address)
    || serializeAspiratorValue(draft.aspirator) !== serializeAspiratorValue(baselineDraft.aspirator)
    || serializeOntarioValue(draft.ontario) !== serializeOntarioValue(baselineDraft.ontario)
    || normalizeOptionalString(draft.building) !== normalizeOptionalString(baselineDraft.building)
    || normalizeOptionalString(draft.client_code) !== normalizeOptionalString(baselineDraft.client_code)
    || normalizeOptionalString(draft.client_name) !== normalizeOptionalString(baselineDraft.client_name)
    || normalizeOptionalString(draft.comment) !== normalizeOptionalString(baselineDraft.comment)
    || normalizeOptionalString(draft.coop) !== normalizeOptionalString(baselineDraft.coop)
    || serializeDeliveryPeriodValue(draft.delivery_period) !== serializeDeliveryPeriodValue(baselineDraft.delivery_period)
    || normalizeLoadingPriorityDraftValue(draft.delivery_priority) !== normalizeLoadingPriorityDraftValue(baselineDraft.delivery_priority)
    || toDateInputValue(draft.delivery_date) !== toDateInputValue(baselineDraft.delivery_date)
    || toTimeInputValue(draft.delivery_window_start) !== toTimeInputValue(baselineDraft.delivery_window_start)
    || normalizeOptionalString(draft.grain_code) !== normalizeOptionalString(baselineDraft.grain_code)
    || normalizeOptionalString(draft.grain_type) !== normalizeOptionalString(baselineDraft.grain_type)
    || serializePreloadedPreviousDayValue(draft.is_preloaded_previous_day) !== serializePreloadedPreviousDayValue(baselineDraft.is_preloaded_previous_day)
    || serializePreloadedPreviousDayValue(draft.is_dompeur) !== serializePreloadedPreviousDayValue(baselineDraft.is_dompeur)
    || normalizeOptionalString(draft.loading_client_name) !== normalizeOptionalString(baselineDraft.loading_client_name)
    || normalizeLoadingPriorityDraftValue(draft.loading_priority) !== normalizeLoadingPriorityDraftValue(baselineDraft.loading_priority)
    || normalizeOptionalString(draft.loading_silo) !== normalizeOptionalString(baselineDraft.loading_silo)
    || toTimeInputValue(draft.loading_window_start) !== toTimeInputValue(baselineDraft.loading_window_start)
    || normalizeRequiredNumber(draft.quantity_tons) !== normalizeRequiredNumber(baselineDraft.quantity_tons)
    || normalizeOptionalTonneRate(draft.loading_tonne_rate_override) !== normalizeOptionalTonneRate(baselineDraft.loading_tonne_rate_override)
    || normalizeOptionalTonneRate(draft.hourly_rate_override) !== normalizeOptionalTonneRate(baselineDraft.hourly_rate_override)
    || normalizeOptionalTonneRate(draft.fixed_rate_override) !== normalizeOptionalTonneRate(baselineDraft.fixed_rate_override)
    || normalizeOptionalTonneRate(draft.unloading_tonne_rate_override) !== normalizeOptionalTonneRate(baselineDraft.unloading_tonne_rate_override)
    || normalizeOptionalInteger(draft.service_time_minutes) !== normalizeOptionalInteger(baselineDraft.service_time_minutes)
    || normalizeOptionalString(draft.work_order_number) !== normalizeOptionalString(baselineDraft.work_order_number)
  );
}

const CREATE_REQUIRED_FIELD_KEYS = new Set([
  "coop",
  "delivery_date",
  "loading_client_name",
]);

const CREATE_DELIVERY_FIELD_KEY_PATTERN = /^deliveries\.(\d+)\.(client_name|address|grain_type|grain_code|quantity_tons|unloading_tonne_rate_override)$/;

function isCreateFieldEmptyForDisplay(draft, fieldKey) {
  const deliveryFieldMatch = typeof fieldKey === "string"
    ? fieldKey.match(CREATE_DELIVERY_FIELD_KEY_PATTERN)
    : null;

  if (deliveryFieldMatch) {
    const deliveryIndex = Number(deliveryFieldMatch[1]);
    const nestedFieldKey = deliveryFieldMatch[2];
    const delivery = getCreateOrderDeliveries(draft)[deliveryIndex];
    return normalizeOptionalString(delivery?.[nestedFieldKey]) === null;
  }

  if (fieldKey === "delivery_date") {
    return typeof draft?.delivery_date !== "string" || draft.delivery_date.trim().length === 0;
  }

  if (fieldKey === "quantity_tons") {
    return typeof draft?.quantity_tons !== "string" || draft.quantity_tons.trim().length === 0;
  }

  return normalizeOptionalString(draft?.[fieldKey]) === null;
}

export function buildVisibleCreateFormErrors(draft, errors) {
  return Object.fromEntries(
    Object.entries(errors).filter(([fieldKey, message]) => {
      if (!message) {
        return false;
      }

      const isRequiredCreateField = CREATE_REQUIRED_FIELD_KEYS.has(fieldKey)
        || CREATE_DELIVERY_FIELD_KEY_PATTERN.test(fieldKey);

      if (!isRequiredCreateField) {
        return true;
      }

      return !isCreateFieldEmptyForDisplay(draft, fieldKey);
    })
  );
}

export function resolveLoadingSummary(order, clientCorrespondenceOptionsByLookupKey) {
  const resolvedClientName = resolveLoadingClientDisplayValue(
    order?.loading_client_name,
    clientCorrespondenceOptionsByLookupKey,
    order?.loading_client_name,
  );
  const address = resolveLoadingAddressPreview(
    order?.loading_client_name,
    clientCorrespondenceOptionsByLookupKey,
    order?.loading_address || order?.address,
  );

  return {
    address,
    primary: formatTextDisplay(resolvedClientName),
    title: resolvedClientName || undefined,
  };
}

export function resolveOrderSortValue(order, sortKey, clientCorrespondenceOptionsByLookupKey) {
  if (sortKey === "loading_client_name") {
    return resolveLoadingClientDisplayValue(
      order?.loading_client_name,
      clientCorrespondenceOptionsByLookupKey,
      order?.loading_client_name,
    );
  }

  if (sortKey === "address") {
    const deliveryAddress = resolveDeliveryAddressDisplay(order);
    return deliveryAddress.title || deliveryAddress.display;
  }

  if (sortKey === "delivery_date") {
    return toDateInputValue(order?.delivery_date);
  }

  if (sortKey === "loading_window_start" || sortKey === "delivery_window_start") {
    return toTimeInputValue(resolveOrderTimeFieldValue(order, sortKey));
  }

  if (sortKey === "delivery_priority") {
    return normalizeLoadingPriorityDraftValue(resolveOrderDeliveryPriorityValue(order));
  }

  if (sortKey === "quantity_tons") {
    return normalizeRequiredNumber(order?.quantity_tons);
  }

  if (sortKey === "actual_loaded_quantity_tons") {
    return resolveOrderActualLoadedQuantityTons(order);
  }

  if (sortKey === "actual_delivered_quantity_tons") {
    return resolveOrderActualDeliveredQuantityTons(order);
  }

  if (sortKey === "delivered_by_driver_name") {
    const summary = resolveOrderDeliveredByDriverSummary(order);
    return summary.names.length > 0 ? summary.display : null;
  }

  if (sortKey === "is_preloaded_previous_day") {
    return formatPreloadedPreviousDayDisplay(order?.is_preloaded_previous_day);
  }

  if (sortKey === "delivery_period") {
    return normalizeDeliveryPeriodDraftValue(order?.delivery_period) === "oui" ? 1 : 0;
  }

  if (sortKey === "end_of_day_load_action") {
    return resolveEndOfDayLoadTruckLabel(order);
  }

  if (sortKey === "is_dompeur") {
    return formatPreloadedPreviousDayDisplay(order?.is_dompeur);
  }

  if (sortKey === "aspirator") {
    return formatAspiratorDisplay(order?.aspirator);
  }

  if (sortKey === "ontario") {
    return formatOntarioDisplay(order?.ontario);
  }

  if (sortKey === "dispatch_status") {
    return resolveDispatchStatusMeta(order?.dispatch_status).label;
  }

  if (sortKey === "delivery_state") {
    return resolveDeliveryMeta(order).label;
  }

  if (sortKey === "billing_approval_status") {
    return resolveBillingApprovalMeta(order).label;
  }

  if (sortKey === "edition_state") {
    return resolveLockMeta(order).label;
  }

  if (sortKey === "comment") {
    return order?.comment;
  }

  return order?.[sortKey];
}

export function isOrderEndOfDayLoadLocked(order) {
  const markerKey = normalizeOptionalString(order?.end_of_day_load_marker_key);
  const targetTruckId = normalizeOptionalString(order?.end_of_day_load_target_truck_id);

  return Boolean(markerKey && targetTruckId);
}

export function resolveEndOfDayLoadTruckLabel(order) {
  if (!isOrderEndOfDayLoadLocked(order)) {
    return "";
  }
  return normalizeOptionalString(order?.end_of_day_load_target_trailer_name)
    || normalizeOptionalString(order?.end_of_day_load_target_truck_name)
    || normalizeOptionalString(order?.end_of_day_load_target_truck_id)
    || "PM";
}

function getEndOfDayLoadGroupOrderIds(order) {
  const lockedOrderIds = normalizeOrderedStringList(order?.end_of_day_load_order_ids);
  if (lockedOrderIds.length > 0) {
    return lockedOrderIds;
  }
  return normalizeOrderedStringList(order?.end_of_day_load_candidate_order_ids);
}

function getEndOfDayLoadGroupDeliveryIds(order) {
  const lockedDeliveryIds = normalizeOrderedStringList(order?.end_of_day_load_delivery_ids);
  if (lockedDeliveryIds.length > 0) {
    return lockedDeliveryIds;
  }
  return normalizeOrderedStringList(order?.end_of_day_load_candidate_delivery_ids);
}

function getEndOfDayLoadGroupStepIds(order) {
  const lockedStepIds = normalizeOrderedStringList(order?.end_of_day_load_group_step_ids);
  if (lockedStepIds.length > 0) {
    return lockedStepIds;
  }
  return normalizeOrderedStringList(order?.end_of_day_load_candidate_group_step_ids);
}

function getEndOfDayLoadSourceGroupKey(order) {
  return normalizeOptionalString(order?.end_of_day_load_source_group_key)
    || normalizeOptionalString(order?.end_of_day_load_candidate_source_group_key);
}

function getEndOfDayLoadSourceTruckId(order) {
  return normalizeOptionalString(order?.end_of_day_load_source_truck_id)
    || normalizeOptionalString(order?.end_of_day_load_candidate_source_truck_id);
}

function getEndOfDayLoadSourceDispatchDate(order) {
  return normalizeOptionalString(order?.end_of_day_load_source_dispatch_date)
    || toDateInputValue(order?.delivery_date);
}

function getEndOfDayLoadMarkerDispatchDate(order) {
  return normalizeOptionalString(order?.end_of_day_load_marker_dispatch_date);
}

export function buildEndOfDayLoadSourceTruckOptions(assignments = []) {
  const seenTruckIds = new Set();
  return (Array.isArray(assignments) ? assignments : []).reduce((options, assignment) => {
    const truckId = normalizeOptionalString(
      assignment?.truck_id || assignment?.truckId || assignment?.assignmentId || assignment?.id,
    );
    if (
      !truckId
      || seenTruckIds.has(truckId)
      || assignment?.is_active === false
      || assignment?.active === false
      || assignment?.is_dispatch_disabled === true
      || assignment?.isDispatchDisabled === true
    ) {
      return options;
    }

    seenTruckIds.add(truckId);
    options.push({
      truckId,
      truckName: normalizeOptionalString(
        assignment?.truck_name
        || assignment?.truckName
        || assignment?.day_truck_name
        || assignment?.label,
      ) || truckId,
      preferred_grain_type: assignment?.preferred_grain_type,
      // La remorque source livre aussi le Chargé PM : règles et nom d'une cible.
      aspirator: assignment?.aspirator,
      compartmentName: normalizeOptionalString(assignment?.compartment_name),
      is_dompeur: assignment?.is_dompeur,
      ontario: assignment?.ontario,
      truck_type: assignment?.truck_type,
    });
    return options;
  }, []);
}

export function buildEndOfDayLoadOrderTruckIndex(assignments = []) {
  const truckIdByOrderId = {};
  for (const assignment of Array.isArray(assignments) ? assignments : []) {
    const truckId = normalizeOptionalString(assignment?.truck_id);
    for (const item of [...(assignment?.orders || []), ...(assignment?.delivery_items || [])]) {
      const orderId = normalizeOptionalString(item?.order_id);
      if (truckId && orderId) {
        truckIdByOrderId[orderId] = truckId;
      }
    }
  }
  return truckIdByOrderId;
}

const END_OF_DAY_LOAD_PENDING_SOURCE_CHANGES = "pending_source_changes";
const END_OF_DAY_LOAD_PENDING_TRUCK_SOURCE_CHANGES = "pending_truck_source_changes";
const END_OF_DAY_LOAD_STALE_SNAPSHOT = "stale_snapshot";

export function resolveEndOfDayLoadSourceTruckPolicyMessage({
  dispatchDate = "",
  dispatchState = null,
  truck = null,
  truckId = "",
} = {}) {
  const normalizedDispatchDate = normalizeOptionalString(dispatchDate);
  const normalizedTruckId = normalizeOptionalString(truckId);
  if (!normalizedDispatchDate || !normalizedTruckId) {
    return "";
  }
  const words = resolveEndOfDayLoadTrailerWords(truck || { truckName: normalizedTruckId }, "source");
  if (dispatchState?.dispatchDate !== normalizedDispatchDate || dispatchState?.isLoading) {
    return `Vérification des M-à-J commandes ${words.of}...`;
  }
  if (dispatchState?.isStaleSnapshot !== true) {
    return "";
  }

  const sourceChangeTruckPolicy = dispatchState?.sourceChangeTruckPolicy;
  const hasExplicitTruckPolicy = Boolean(
    sourceChangeTruckPolicy
    && typeof sourceChangeTruckPolicy === "object"
    && !Array.isArray(sourceChangeTruckPolicy)
    && Object.prototype.hasOwnProperty.call(sourceChangeTruckPolicy, normalizedTruckId),
  );
  if (hasExplicitTruckPolicy && sourceChangeTruckPolicy[normalizedTruckId] === null) {
    return "";
  }

  if (
    hasExplicitTruckPolicy
    && sourceChangeTruckPolicy[normalizedTruckId] === END_OF_DAY_LOAD_PENDING_SOURCE_CHANGES
  ) {
    return `${words.The} contient une ou plusieurs commandes touchées par les M-à-J en attente. Effectuez les M-à-J commandes avant d'utiliser ${words.this}.`;
  }
  if (
    hasExplicitTruckPolicy
    && sourceChangeTruckPolicy[normalizedTruckId] === END_OF_DAY_LOAD_PENDING_TRUCK_SOURCE_CHANGES
  ) {
    return `${words.The} a des modifications en attente. Consultez « Mise à jour » (Camions, Remorques ou Chauffeurs) du dispatch Jour du ${normalizedDispatchDate}, puis réessayez.`;
  }
  if (
    hasExplicitTruckPolicy
    && sourceChangeTruckPolicy[normalizedTruckId] === END_OF_DAY_LOAD_STALE_SNAPSHOT
  ) {
    return `Le dispatch Jour du ${normalizedDispatchDate} est obsolète pour ${words.the}. Relancez l'optimisation de cette date avant d'utiliser ${words.this}.`;
  }
  return `Impossible de vérifier l'état ${words.of}. Rechargez le dispatch, puis réessayez.`;
}

function getEndOfDayLoadLoadingLabel(order) {
  return normalizeOptionalString(order?.end_of_day_load_loading_label)
    || normalizeOptionalString(order?.end_of_day_load_candidate_loading_label);
}

export function resolveEndOfDayLoadDisplayLabel(order) {
  const truckLabel = resolveEndOfDayLoadTruckLabel(order);
  const groupOrderCount = getEndOfDayLoadGroupOrderIds(order).length;
  if (!truckLabel || groupOrderCount <= 1) {
    return truckLabel;
  }
  return `${truckLabel} (${groupOrderCount})`;
}

function resolveEndOfDayLoadTruckLabelFromContext({
  truckId = "",
  truckName = "",
  truckLabelById = null,
  fallbackLabel = "",
} = {}) {
  const normalizedTruckId = normalizeOptionalString(truckId);
  const normalizedTruckName = normalizeOptionalString(truckName);
  if (normalizedTruckName) {
    return normalizedTruckName;
  }
  if (normalizedTruckId && truckLabelById && typeof truckLabelById === "object") {
    const lookupLabel = normalizeOptionalString(truckLabelById[normalizedTruckId]);
    if (lookupLabel) {
      return lookupLabel;
    }
  }
  if (normalizedTruckId) {
    return normalizedTruckId;
  }
  return normalizeOptionalString(fallbackLabel) || "—";
}

function normalizeEndOfDayLoadModeValue(value = "") {
  const normalizedValue = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (normalizedValue === "evening" || normalizedValue === "soir" || normalizedValue === "livrer_soir") {
    return "evening";
  }
  if (
    normalizedValue === "next_day"
    || normalizedValue === "tomorrow"
    || normalizedValue === "demain"
    || normalizedValue === "day"
    || normalizedValue === "jour"
    || normalizedValue === "livrer_demain"
  ) {
    return "next_day";
  }
  return "";
}

function normalizeEndOfDayLoadDisplayModeValue(value = "") {
  return normalizeEndOfDayLoadModeValue(value);
}

function resolveEndOfDayLoadDeliveryModeLabel(displayMode = "", deliveryMode = "next_day", {
  sourceDispatchDate = "",
  targetDispatchDate = "",
  todayDate = "",
} = {}) {
  const normalizedDisplayMode = normalizeEndOfDayLoadDisplayModeValue(displayMode);
  const normalizedDeliveryMode = normalizeEndOfDayLoadModeValue(deliveryMode) || "next_day";
  return getEndOfDayLoadModeLongLabel(normalizedDisplayMode || normalizedDeliveryMode, {
    sourceDispatchDate,
    targetDispatchDate,
    todayDate,
  });
}

function resolveEndOfDayLoadDeliveryModeShortLabel(displayMode = "", deliveryMode = "next_day", {
  sourceDispatchDate = "",
  targetDispatchDate = "",
  todayDate = "",
} = {}) {
  const normalizedDisplayMode = normalizeEndOfDayLoadDisplayModeValue(displayMode);
  const normalizedDeliveryMode = normalizeEndOfDayLoadModeValue(deliveryMode) || "next_day";
  return getEndOfDayLoadModeShortLabel(normalizedDisplayMode || normalizedDeliveryMode, {
    sourceDispatchDate,
    targetDispatchDate,
    todayDate,
  });
}

function normalizeEndOfDayLoadDateValue(value = "") {
  return typeof value === "string" ? value.trim().slice(0, 10) : "";
}

function resolveEndOfDayLoadDeliveryModeFromContext({
  deliveryMode = "",
  sourceDispatchDate = "",
  targetDispatchDate = "",
  targetPeriod = "",
} = {}) {
  const normalizedDeliveryMode = normalizeEndOfDayLoadModeValue(deliveryMode);
  if (normalizedDeliveryMode) {
    return normalizedDeliveryMode;
  }

  const normalizedTargetPeriod = normalizeEndOfDayLoadModeValue(targetPeriod);
  if (normalizedTargetPeriod) {
    return normalizedTargetPeriod;
  }

  const normalizedSourceDispatchDate = normalizeEndOfDayLoadDateValue(sourceDispatchDate);
  const normalizedTargetDispatchDate = normalizeEndOfDayLoadDateValue(targetDispatchDate);
  return normalizedSourceDispatchDate
    && normalizedTargetDispatchDate
    && normalizedSourceDispatchDate === normalizedTargetDispatchDate
    ? "evening"
    : "next_day";
}

function buildEndOfDayLoadHoverCardDetails({
  deliveryMode = "",
  displayMode = "",
  sourceDispatchDate = "",
  targetDispatchDate = "",
  sourceTruckId = "",
  sourceTruckName = "",
  targetTruckId = "",
  targetTruckName = "",
  targetPeriod = "",
  todayDate = "",
  truckLabelById = null,
  rowAssignmentId = "",
  rowTruckLabel = "",
  rowTrailerName = "",
  sourceTrailerName = "",
  targetTrailerName = "",
  groupOrderIds = [],
} = {}) {
  const resolvedTargetTruckId = normalizeOptionalString(targetTruckId) || normalizeOptionalString(rowAssignmentId);
  const resolvedSourceTruckId = normalizeOptionalString(sourceTruckId)
    || (
      resolvedTargetTruckId
      && normalizeOptionalString(rowAssignmentId)
      && resolvedTargetTruckId === normalizeOptionalString(rowAssignmentId)
        ? normalizeOptionalString(rowAssignmentId)
        : ""
    );
  const normalizedGroupOrderIds = normalizeComparableStringList(groupOrderIds);

  const resolvedDeliveryMode = resolveEndOfDayLoadDeliveryModeFromContext({
    deliveryMode,
    sourceDispatchDate,
    targetDispatchDate,
    targetPeriod,
  });
  const resolvedDisplayMode = normalizeEndOfDayLoadDisplayModeValue(displayMode) || resolvedDeliveryMode;
  // Camions et remorques indépendants : le Chargé PM se nomme par sa remorque ; la ligne du segment connaît la sienne.
  const resolveTrailerName = (trailerName, truckId) => normalizeOptionalString(trailerName)
    || (truckId && truckId === normalizeOptionalString(rowAssignmentId) ? normalizeOptionalString(rowTrailerName) : "");
  const sourceTrailer = resolveTrailerName(sourceTrailerName, resolvedSourceTruckId);
  const targetTrailer = resolveTrailerName(targetTrailerName, resolvedTargetTruckId);

  const details = {
    deliveryMode: resolvedDeliveryMode,
    deliveryModeDisplayMode: resolvedDisplayMode,
    initialDeliveryDate: formatDateDisplay(sourceDispatchDate),
    sourceFieldLabel: sourceTrailer ? "Remorque source" : "Camion source",
    sourceTruckLabel: sourceTrailer || resolveEndOfDayLoadTruckLabelFromContext({
      truckId: resolvedSourceTruckId,
      truckName: sourceTruckName,
      truckLabelById,
      fallbackLabel: resolvedSourceTruckId === normalizeOptionalString(rowAssignmentId) ? rowTruckLabel : "",
    }),
    targetFieldLabel: targetTrailer ? "Remorque cible" : "Camion cible",
    targetTruckLabel: targetTrailer || resolveEndOfDayLoadTruckLabelFromContext({
      truckId: resolvedTargetTruckId,
      truckName: targetTruckName,
      truckLabelById,
      fallbackLabel: rowTruckLabel,
    }),
    targetDeliveryDate: formatDateDisplay(targetDispatchDate),
  };
  details.deliveryModeLabel = resolveEndOfDayLoadDeliveryModeLabel(resolvedDisplayMode, resolvedDeliveryMode, {
    sourceDispatchDate,
    targetDispatchDate,
    todayDate,
  });
  details.deliveryModeShortLabel = resolveEndOfDayLoadDeliveryModeShortLabel(resolvedDisplayMode, resolvedDeliveryMode, {
    sourceDispatchDate,
    targetDispatchDate,
    todayDate,
  });

  if (normalizedGroupOrderIds.length > 1) {
    details.groupOrderCount = normalizedGroupOrderIds.length;
    details.groupOrderPreview = normalizedGroupOrderIds.slice(0, 3).join(" · ");
  }

  return details;
}

export function resolveEndOfDayLoadHoverCardDetailsFromSegment(segment, {
  row = null,
  todayDate = "",
  truckLabelById = null,
} = {}) {
  if (!segment?.isEndOfDayLoad && !segment?.isEndOfDayLoadSourcePreload) {
    return null;
  }

  return buildEndOfDayLoadHoverCardDetails({
    sourceDispatchDate: segment?.endOfDayLoadSourceDispatchDate,
    targetDispatchDate: segment?.endOfDayLoadTargetDispatchDate,
    sourceTruckId: segment?.endOfDayLoadSourceTruckId,
    sourceTruckName: segment?.endOfDayLoadSourceTruckName,
    targetTruckId: segment?.endOfDayLoadTargetTruckId,
    targetTruckName: segment?.endOfDayLoadTargetTruckName,
    deliveryMode: segment?.endOfDayLoadDeliveryMode,
    displayMode: segment?.endOfDayLoadDisplayMode,
    targetPeriod: segment?.endOfDayLoadTargetPeriod,
    todayDate,
    truckLabelById,
    rowAssignmentId: row?.assignmentId,
    rowTruckLabel: row?.truckLabel,
    rowTrailerName: row?.assignment?.compartment_name,
    groupOrderIds: segment?.endOfDayLoadOrderIds,
  });
}

export function resolveEndOfDayLoadHoverCardDetails(order, {
  todayDate = "",
} = {}) {
  if (!isOrderEndOfDayLoadLocked(order)) {
    return null;
  }

  return buildEndOfDayLoadHoverCardDetails({
    sourceDispatchDate: order?.end_of_day_load_source_dispatch_date,
    targetDispatchDate: order?.end_of_day_load_target_dispatch_date,
    sourceTruckId: order?.end_of_day_load_source_truck_id,
    sourceTruckName: order?.end_of_day_load_source_truck_name,
    targetTruckId: order?.end_of_day_load_target_truck_id,
    targetTruckName: order?.end_of_day_load_target_truck_name,
    sourceTrailerName: order?.end_of_day_load_source_trailer_name,
    targetTrailerName: order?.end_of_day_load_target_trailer_name,
    deliveryMode: order?.end_of_day_load_delivery_mode,
    displayMode: order?.end_of_day_load_display_mode,
    targetPeriod: order?.end_of_day_load_target_period,
    todayDate,
    groupOrderIds: order?.end_of_day_load_order_ids,
  });
}

export function resolveEndOfDayLoadTitle(order, options = {}) {
  const details = resolveEndOfDayLoadHoverCardDetails(order, options);
  if (!details) {
    const candidateOrderIds = normalizeOrderedStringList(order?.end_of_day_load_candidate_order_ids);
    if (candidateOrderIds.length > 1) {
      return `Assigner Chargé en PM pour le groupe complet (${candidateOrderIds.join(", ")})`;
    }
    return "Assigner Chargé en PM";
  }

  return [
    `Date source : ${details.initialDeliveryDate}`,
    `${details.sourceFieldLabel} : ${details.sourceTruckLabel}`,
    `Date cible : ${details.targetDeliveryDate}`,
    `${details.targetFieldLabel} : ${details.targetTruckLabel}`,
    details.deliveryModeLabel ? `Mode : ${details.deliveryModeLabel}` : "",
    details.groupOrderCount > 1
      ? `Groupe PM : ${details.groupOrderCount} commandes${details.groupOrderPreview ? ` (${details.groupOrderPreview})` : ""}`
      : "",
  ].filter(Boolean).join(" · ");
}

export function resolveSelectedEndOfDayLoadGroupAction(orders = [], selectedOrderIds = {}) {
  const selectedOrders = (Array.isArray(orders) ? orders : [])
    .filter((order) => order?.order_id && selectedOrderIds[order.order_id]);
  if (selectedOrders.length === 0) {
    return {
      canApply: false,
      reason: "Sélectionnez un groupe PM complet.",
      selectedCount: 0,
      targetTruckId: "",
    };
  }
  if (selectedOrders.length < 2) {
    return {
      canApply: false,
      reason: "Sélectionnez le groupe PM complet.",
      selectedCount: selectedOrders.length,
      targetTruckId: normalizeOptionalString(selectedOrders[0]?.end_of_day_load_target_truck_id) || "",
    };
  }

  const firstOrder = selectedOrders[0];
  const sourceGroupKey = getEndOfDayLoadSourceGroupKey(firstOrder);
  const groupStepIds = getEndOfDayLoadGroupStepIds(firstOrder);
  const groupOrderIds = getEndOfDayLoadGroupOrderIds(firstOrder);
  const groupDeliveryIds = getEndOfDayLoadGroupDeliveryIds(firstOrder);
  const sourceTruckId = getEndOfDayLoadSourceTruckId(firstOrder);
  const sourceDispatchDate = getEndOfDayLoadSourceDispatchDate(firstOrder);
  const targetDispatchDate = normalizeOptionalString(firstOrder?.end_of_day_load_target_dispatch_date);
  const targetTruckId = normalizeOptionalString(firstOrder?.end_of_day_load_target_truck_id) || "";
  const markerKey = normalizeOptionalString(firstOrder?.end_of_day_load_marker_key) || "";
  const markerDispatchDate = markerKey
    ? (getEndOfDayLoadMarkerDispatchDate(firstOrder) || sourceDispatchDate)
    : "";
  const dispatchDate = markerDispatchDate || sourceDispatchDate;

  if (!sourceGroupKey || groupStepIds.length === 0 || groupOrderIds.length < 2 || groupDeliveryIds.length < 2 || !sourceTruckId) {
    return {
      canApply: false,
      reason: "Groupe de chargement complet non prouvé.",
      selectedCount: selectedOrders.length,
      targetTruckId,
    };
  }

  const selectedOrderIdList = normalizeComparableStringList(selectedOrders.map((order) => order.order_id));
  if (!stringListsMatch(selectedOrderIdList, groupOrderIds)) {
    return {
      canApply: false,
      reason: "Sélectionnez exactement le groupe de chargement.",
      selectedCount: selectedOrders.length,
      targetTruckId,
    };
  }

  const groupMismatch = selectedOrders.some((order) => (
    getEndOfDayLoadSourceGroupKey(order) !== sourceGroupKey
    || getEndOfDayLoadSourceTruckId(order) !== sourceTruckId
    || getEndOfDayLoadSourceDispatchDate(order) !== sourceDispatchDate
    || !stringListsMatch(getEndOfDayLoadGroupStepIds(order), groupStepIds)
    || !stringListsMatch(getEndOfDayLoadGroupOrderIds(order), groupOrderIds)
    || !stringListsMatch(getEndOfDayLoadGroupDeliveryIds(order), groupDeliveryIds)
    || (markerKey && normalizeOptionalString(order?.end_of_day_load_marker_key) !== markerKey)
    || (markerKey && (getEndOfDayLoadMarkerDispatchDate(order) || sourceDispatchDate) !== markerDispatchDate)
  ));
  if (groupMismatch) {
    return {
      canApply: false,
      reason: "Sélection de groupe incohérente.",
      selectedCount: selectedOrders.length,
      targetTruckId,
    };
  }

  return {
    canApply: Boolean(dispatchDate),
    deliveryIds: groupDeliveryIds,
    dispatchDate,
    groupStepIds,
    label: getEndOfDayLoadLoadingLabel(firstOrder) || `${groupOrderIds.length} commandes`,
    markerDispatchDate,
    markerKey,
    orderIds: groupOrderIds,
    reason: dispatchDate ? "" : "Date source manquante.",
    selectedCount: selectedOrders.length,
    sourceDispatchDate,
    sourceGroupKey,
    sourceTruckId,
    targetDispatchDate,
    targetTruckId,
  };
}

export function resolveEndOfDayLoadGroupActionFromOrder(order, orders = []) {
  const groupOrderIds = getEndOfDayLoadGroupOrderIds(order);
  if (groupOrderIds.length <= 1 || !groupOrderIds.includes(normalizeOptionalString(order?.order_id))) {
    return null;
  }

  const selectedOrderIds = Object.fromEntries(groupOrderIds.map((orderId) => [orderId, true]));
  const groupAction = resolveSelectedEndOfDayLoadGroupAction(orders, selectedOrderIds);
  if (groupAction?.canApply) {
    return groupAction;
  }

  // La recherche peut masquer les autres commandes du groupe : la ligne cliquée porte tout le groupe.
  const markerKey = normalizeOptionalString(order?.end_of_day_load_marker_key) || "";
  const sourceGroupKey = getEndOfDayLoadSourceGroupKey(order);
  const groupStepIds = getEndOfDayLoadGroupStepIds(order);
  const deliveryIds = getEndOfDayLoadGroupDeliveryIds(order);
  const sourceTruckId = getEndOfDayLoadSourceTruckId(order);
  const sourceDispatchDate = getEndOfDayLoadSourceDispatchDate(order);
  const markerDispatchDate = markerKey ? (getEndOfDayLoadMarkerDispatchDate(order) || sourceDispatchDate) : "";
  const dispatchDate = markerDispatchDate || sourceDispatchDate;
  if (!sourceGroupKey || groupStepIds.length === 0 || deliveryIds.length < 2 || !sourceTruckId) {
    return null;
  }

  return {
    canApply: Boolean(dispatchDate),
    deliveryIds,
    dispatchDate,
    groupStepIds,
    label: getEndOfDayLoadLoadingLabel(order) || `${groupOrderIds.length} commandes`,
    markerDispatchDate,
    markerKey,
    orderIds: groupOrderIds,
    reason: dispatchDate ? "" : "Date source manquante.",
    selectedCount: groupOrderIds.length,
    sourceDispatchDate,
    sourceGroupKey,
    sourceTruckId,
    targetDispatchDate: normalizeOptionalString(order?.end_of_day_load_target_dispatch_date),
    targetTruckId: normalizeOptionalString(order?.end_of_day_load_target_truck_id),
  };
}

export function resolveDeliverySummary(order) {
  const deliveries = getOrderDeliveries(order);

  if (deliveries.length <= 1) {
    const delivery = deliveries[0] || null;
    const primary = formatTextDisplay(delivery?.client_name || order?.client_name);
    const secondary = formatTextDisplay(delivery?.address);
    const title = [delivery?.client_name || order?.client_name, delivery?.address].filter(Boolean).join("\n") || undefined;
    return { primary, secondary, title };
  }

  const labels = deliveries
    .map((delivery) => (typeof delivery?.client_name === "string" && delivery.client_name.trim() ? delivery.client_name.trim() : delivery?.delivery_id))
    .filter(Boolean);
  const preview = labels.slice(0, 2).join(" · ");
  const extraCount = Math.max(0, labels.length - 2);
  const primary = `${deliveries.length} déchargements`;
  const secondary = preview ? `${preview}${extraCount > 0 ? ` +${extraCount}` : ""}` : "—";
  const title = deliveries
    .map((delivery) => [delivery?.client_name || delivery?.delivery_id, delivery?.address].filter(Boolean).join(" · "))
    .filter(Boolean)
    .join("\n") || undefined;
  return { primary, secondary, title };
}

export function resolveOrderActualLoadedQuantityTons(order) {
  const value = order?.actual_loaded_quantity_tons;
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "string" && value.trim() === "") {
    return null;
  }

  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return null;
  }

  return parsedValue;
}

export function resolveOrderActualDeliveredQuantityTons(order) {
  const deliveries = Array.isArray(order?.deliveries) ? order.deliveries.filter(Boolean) : [];
  let hasActualDeliveredQuantity = false;
  let totalActualDeliveredQuantity = 0;

  deliveries.forEach((delivery) => {
    const rawActualQuantity = delivery?.actual_delivered_quantity_tons;
    if (
      rawActualQuantity === null
      || rawActualQuantity === undefined
      || (typeof rawActualQuantity === "string" && rawActualQuantity.trim() === "")
    ) {
      return;
    }

    const deliveryActualQuantity = Number(rawActualQuantity);
    if (!Number.isFinite(deliveryActualQuantity) || deliveryActualQuantity < 0) {
      return;
    }

    hasActualDeliveredQuantity = true;
    totalActualDeliveredQuantity += deliveryActualQuantity;
  });

  if (!hasActualDeliveredQuantity) {
    return null;
  }

  return totalActualDeliveredQuantity;
}

export function resolveDeliveryAddressDisplay(order) {
  const addresses = getOrderDeliveries(order)
    .map((delivery) => (typeof delivery?.address === "string" ? delivery.address.trim() : ""))
    .filter(Boolean);
  const uniqueAddresses = Array.from(new Set(addresses));

  if (uniqueAddresses.length === 1) {
    return {
      display: uniqueAddresses[0],
      title: uniqueAddresses[0],
    };
  }

  if (uniqueAddresses.length > 1) {
    const preview = uniqueAddresses.slice(0, 2).join(", ");
    return {
      display: uniqueAddresses.length > 2 ? `${preview} +${uniqueAddresses.length - 2}` : preview,
      title: uniqueAddresses.join("\n"),
    };
  }

  const fallback = typeof order?.address === "string" ? order.address : "";
  return {
    display: formatTextDisplay(fallback),
    title: fallback || undefined,
  };
}

export function resolveReadonlyTextCell(order, column) {
  if (column.key === "address") {
    return resolveDeliveryAddressDisplay(order);
  }

  if (column.key === "actual_loaded_quantity_tons") {
    const actualLoadedQuantityTons = resolveOrderActualLoadedQuantityTons(order);
    const display = actualLoadedQuantityTons == null ? "—" : formatQuantityTons(actualLoadedQuantityTons);
    return {
      display,
      title: display !== "—" ? display : undefined,
    };
  }

  if (column.key === "actual_delivered_quantity_tons") {
    const actualDeliveredQuantityTons = resolveOrderActualDeliveredQuantityTons(order);
    const display = actualDeliveredQuantityTons == null ? "—" : formatQuantityTons(actualDeliveredQuantityTons);
    return {
      display,
      title: display !== "—" ? display : undefined,
    };
  }

  if (column.key === "delivered_by_driver_name") {
    const summary = resolveOrderDeliveredByDriverSummary(order);
    return {
      display: summary.display,
      title: summary.title,
    };
  }

  if (column.key === "delivery_priority") {
    if (!canEditSingleDeliveryWindowFields(order)) {
      return {
        display: "Multi",
        title: ORDER_DELIVERY_SCOPE_TITLE,
      };
    }

    const display = formatTextDisplay(normalizeLoadingPriorityDraftValue(resolveOrderDeliveryPriorityValue(order)) || null);
    return {
      display,
      title: display !== "—" ? display : undefined,
    };
  }

  if (column.key === "aspirator") {
    const display = formatAspiratorDisplay(order?.aspirator);
    return {
      display,
      title: display,
    };
  }

  if (column.key === "ontario") {
    const display = formatOntarioDisplay(order?.ontario);
    return {
      display,
      title: display,
    };
  }

  if (column.key === "is_preloaded_previous_day") {
    const display = formatPreloadedPreviousDayDisplay(order?.is_preloaded_previous_day);
    return {
      display,
      title: display,
    };
  }

  if (column.key === "delivery_period") {
    const display = formatDeliveryPeriodDisplay(order?.delivery_period);
    return {
      display,
      title: display,
    };
  }

  if (column.key === "is_dompeur") {
    const display = formatPreloadedPreviousDayDisplay(order?.is_dompeur);
    return {
      display,
      title: display,
    };
  }

  // « Facturer à » figé par un relevé ou un préchargement validé : la bulle dit comment le changer, comme dans la Consolidation.
  if (column.key === "coop" && order?.can_edit_coop === false && !order?.is_cancelled) {
    return {
      display: formatTextDisplay(order?.coop),
      title: `${order?.coop || "—"} · Rouvrez le relevé ou le préchargement validé pour changer « Facturer à ».`,
    };
  }

  const rawValue = order?.[column.key];
  return {
    display: formatTextDisplay(rawValue),
    title: typeof rawValue === "string" ? rawValue : undefined,
  };
}

export function formatAspiratorDisplay(value) {
  return normalizeAspiratorDraftValue(value) === "oui" ? "Oui" : "Non";
}

export function formatOntarioDisplay(value) {
  return normalizeOntarioDraftValue(value) === "oui" ? "Oui" : "Non";
}

export function formatPreloadedPreviousDayDisplay(value) {
  return normalizePreloadedPreviousDayDraftValue(value) === "oui" ? "Oui" : "Non";
}

export function formatDeliveryPeriodDisplay(value) {
  return normalizeDeliveryPeriodDraftValue(value) === "oui" ? "Oui" : "Non";
}
